import {
  Fragment,
  useEffect,
  useMemo,
  useRef,
  useState,
  type CSSProperties,
  type ReactNode,
} from "react";
import { createPortal } from "react-dom";
import { useNavigate } from "react-router-dom";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import {
  BOOKING_MODES,
  BOOKING_MODE_LABELS,
  DEFAULT_BOOKING_SHOW,
  DEFAULT_GUEST_FIELDS,
  EXTRA_TERM_KINDS,
  EXTRA_TERM_KIND_LABELS,
  GUEST_FIELD_CATALOG,
  GUEST_INPUT_KINDS,
  GUEST_INPUT_KIND_LABELS,
  GUEST_INPUT_WITH_OPTIONS,
  CONTRACT_STATUS_MAP,
  INVOICE_STATUS_MAP,
  QUOTATION_STATUS_MAP,
  durationLabel,
  formatPhone,
  extraTermCost,
  type BookingAnswer,
  type BookingDay,
  type BookingExtraTerm,
  type BookingLayout,
  type BookingShow,
  type BookingShowChapter,
  type BookingGuestField,
  type ExtraTermKind,
  type GuestInputKind,
  type PublicBookingPage,
  SLOT_MINUTES,
  bookingUrl,
  type BookingMedia,
  type BookingPurpose,
  type BookingPageView,
  type CalendarKind,
} from "@goyone/shared";
import { api } from "../../lib/api";
import {
  date,
  dateTime,
  longDay,
  money,
  plural,
  time,
  WORDS,
} from "../../lib/format";
import { useSession } from "../../auth/AuthProvider";
import { Icon } from "../../components/Icon";
import {
  Badge,
  EmptyState,
  ErrorState,
  Modal,
  PageHead,
  SearchBox,
} from "../../components/ui";
import { Menu } from "../../components/Menu";
import { useTeamDirectory } from "../../components/People";
import { useTenantProfile } from "../../lib/settings";
import { ReachIcons } from "../../components/Reach";
import { DocPreview } from "../../components/DocPreview";
import { useToast } from "../../components/Toast";

/**
 * أنواع صفحات الحجز — تصنيفٌ يصف العمل لا يغيّر البيانات.
 *
 * النوع يُكتب مع الصفحة مرّةً ويُقرأ بعدها: به يُهيَّأ النموذج ويُخفى
 * ما لا يخصّه — فالمجانيةُ بلا مال، والمكانُ بلا صاحب وقت.
 */
type PageKind = "FREE_CONSULT" | "PAID_CONSULT" | "PLACE";

const PAGE_KINDS: ReadonlyArray<{
  key: PageKind;
  label: string;
  hint: string;
  icon: "video" | "wallet" | "mapPin";
}> = [
  {
    key: "FREE_CONSULT",
    label: "حجز استشارة مجانية",
    hint: "موعدٌ بمدّةٍ ثابتة، أونلاين أو حضوريًّا — بلا سعرٍ ولا مقدَّم.",
    icon: "video",
  },
  {
    key: "PAID_CONSULT",
    label: "حجز استشارة مدفوعة",
    hint: "كالمجانية، وفيها سعرٌ مُعلَن ومقدَّرٌ مقدَّمًا إن أردت.",
    icon: "wallet",
  },
  {
    key: "PLACE",
    label: "حجز مكان",
    hint: "استوديو أو قاعة: العميل يختار المدّة ضمن سقف، والسعر لكل وحدةٍ زمنية.",
    icon: "mapPin",
  },
];

/**
 * لونُ كلّ نوعٍ — علامةٌ لا غسل.
 *
 * يظهر على حافة الأقسام ورقاقات أيقوناتها وفي معاينة الصفحة، ولا يُصبغ
 * به النموذج كلُّه. والألوان من لوحة الاجتماعات المُتحقَّق منها، فلا
 * لونَ خامسًا يُخترع هنا.
 */
const KIND_TONE: Readonly<Record<PageKind, string>> = {
  FREE_CONSULT: "var(--mv-2)",
  PAID_CONSULT: "var(--mv-4)",
  PLACE: "var(--mv-3)",
};

/**
 * نوعُ صفحةٍ قائمة — يُقرأ من عمودها.
 *
 * كان يُستنتج من السعر، فلمّا صار السعر للغرض لا للصفحة صارت المدفوعةُ
 * تُفتح مجانيةً وتُخفى حقولُ مالها. فالنوع يُكتب عند الإنشاء ويُقرأ بعدها،
 * والاستنتاجُ يبقى لصفٍّ قديمٍ لم يُكتب له نوعٌ بعد.
 */
function kindOf(p: BookingPageView): PageKind {
  if (p.kind && p.kind !== "FREE_CONSULT") return p.kind;
  if (p.durationMode === "RANGE") return "PLACE";
  const paid = p.purposes.some((x) => x.price != null) || p.depositPercent > 0;
  return paid ? "PAID_CONSULT" : (p.kind ?? "FREE_CONSULT");
}
/**
 * صفحات الحجز.
 *
 * كل صفحة رابطٌ يُرسَل إلى عميل فيختار موعدًا شاغرًا بنفسه. والشاشة همّها
 * الأول أن يُنسخ الرابط: صفحةٌ لا يجد صاحبها رابطها لا تُستعمل.
 */

interface CalendarRow {
  id: string;
  name: string;
  kind: CalendarKind;
  isActive: boolean;
}

export function BookingPages() {
  const queryClient = useQueryClient();
  const toast = useToast();
  const session = useSession();
  const [editing, setEditing] = useState<BookingPageView | null>(null);
  /*
   * النوع يُنتقى قبل النموذج.
   *
   * الصفحات ثلاثةُ أنواعٍ في العمل: استشارةٌ مجانية، واستشارةٌ
   * مدفوعة، وحجزُ مكان. وكانت كلُّها نموذجًا واحدًا يعرض حقول المال
   * والمكان والمدى على من لا يريدها — فيُملأ ما لا يلزم ويُنسى ما يلزم.
   * والانتقاءُ أولًا يُهيّئ الحقول ويُخفي ما لا يخصّ النوع.
   */
  const [creating, setCreating] = useState<PageKind | null>(null);
  const [choosing, setChoosing] = useState(false);
  const [viewing, setViewing] = useState<BookingPageView | null>(null);
  const [search, setSearch] = useState("");
  /* الترشيح بالنوع — والكلُّ افتراضًا. */
  const [kindFilter, setKindFilter] = useState<PageKind | "">("");

  const pages = useQuery({
    queryKey: ["booking", "pages"],
    queryFn: () => api<BookingPageView[]>("/booking/pages"),
  });

  const remove = useMutation({
    mutationFn: (id: string) =>
      api(`/booking/pages/${id}`, { method: "DELETE" }),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ["booking"] });
      toast("حُذفت الصفحة");
    },
    onError: (e) => toast(e instanceof Error ? e.message : "تعذّر الحذف."),
  });

  const toggle = useMutation({
    mutationFn: (p: BookingPageView) =>
      api(`/booking/pages/${p.id}`, {
        method: "PATCH",
        body: { isActive: !p.isActive },
      }),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ["booking"] });
      toast("حُدّثت الصفحة");
    },
    onError: (e) => toast(e instanceof Error ? e.message : "تعذّر التحديث."),
  });

  const copy = async (p: BookingPageView) => {
    const url = bookingUrl(window.location.origin, session.tenant.slug, p.slug);
    try {
      await navigator.clipboard.writeText(url);
      toast("نُسخ الرابط");
    } catch {
      /*
       * النسخ قد يُمنع: الحافظة تحتاج سياقًا آمنًا وإذنًا.
       * والرابط يُعرض عندها ليُنسخ باليد — أهون من رسالة فشلٍ بلا مخرج.
       */
      window.prompt("انسخ الرابط:", url);
    }
  };

  const rows = pages.data ?? [];
  /* ما يُعرض بعد البحث والترشيح — والعدّادات تُحسب على الكلّ لا على المعروض. */
  const shown = rows.filter(
    (p) =>
      (!kindFilter || kindOf(p) === kindFilter) &&
      (!search.trim() ||
        [p.name, p.slug, p.description, p.calendarName, p.ownerName].some(
          (f) =>
            f && String(f).toLowerCase().includes(search.trim().toLowerCase()),
        )),
  );

  return (
    <div className="page">
      <PageHead
        title="صفحات الحجز"
        subtitle="روابط يختار منها العميل موعده — وتفتح صفقتَه وعرضَ سعره"
        actions={
          <button className="btn btn-primary" onClick={() => setChoosing(true)}>
            <Icon name="plus" size={15} />
            صفحة جديدة
          </button>
        }
      />

      {pages.isError && (
        <ErrorState error={pages.error} onRetry={() => void pages.refetch()} />
      )}

      {/*
        رابط محلي لا يفتح عند العميل.
        من يشارك localhost يشارك جهازه — والتنبيه هنا أهون من عميلٍ يتصل
        قائلًا «الرابط لا يعمل».
      */}
      {/^(localhost|127[.])/.test(window.location.hostname) &&
        rows.length > 0 && (
          <div className="alert alert-info mb-4">
            النظام يعمل محليًّا الآن، فالرابط يفتح على هذا الجهاز فقط. عند نشر
            النظام على نطاق عام تصبح الروابط صالحة للإرسال إلى العملاء.
          </div>
        )}

      {pages.data && rows.length === 0 && (
        <EmptyState
          title="لا صفحات حجز بعد"
          text="أنشئ صفحة، وأرسل رابطها لعميل بدل أن تتبادلا الرسائل لتحديد موعد."
        />
      )}

      {rows.length > 0 && (
        <>
          {/* ما تقوله الصفحاتُ مجتمعةً: كم صفحةً تستقبل، وكم حجزًا جاء منها. */}
          <div className="bkp-stats">
            <div className="bkp-stat">
              <span className="bkp-stat-ic">
                <Icon name="send" size={15} />
              </span>
              <span className="bkp-stat-text">
                <strong className="num">{rows.length}</strong>
                <span className="tiny mute">صفحة</span>
              </span>
            </div>
            <div className="bkp-stat">
              <span className="bkp-stat-ic is-ok">
                <Icon name="check" size={15} />
              </span>
              <span className="bkp-stat-text">
                <strong className="num">
                  {rows.filter((p) => p.isActive).length}
                </strong>
                <span className="tiny mute">تستقبل حجوزات</span>
              </span>
            </div>
            <div className="bkp-stat">
              <span className="bkp-stat-ic">
                <Icon name="users" size={15} />
              </span>
              <span className="bkp-stat-text">
                <strong className="num">
                  {rows.reduce((n, p) => n + p.bookedCount, 0)}
                </strong>
                <span className="tiny mute">حجزًا وصل</span>
              </span>
            </div>
            <div className="bkp-stat">
              <span className="bkp-stat-ic">
                <Icon name="calendar" size={15} />
              </span>
              <span className="bkp-stat-text">
                <strong className="num">
                  {
                    new Set(rows.map((p) => p.calendarName).filter(Boolean))
                      .size
                  }
                </strong>
                <span className="tiny mute">تقويمًا تكتب فيه</span>
              </span>
            </div>
          </div>

          {/* بحثٌ بالشكل الموحّد، وترشيحٌ بالنوع أقراصًا. */}
          <div className="tab-tools bkp-tools">
            <SearchBox
              value={search}
              onChange={setSearch}
              placeholder="ابحث باسم الصفحة أو رابطها أو تقويمها"
            />
            <div className="bkp-filters">
              <button
                type="button"
                className={`chip${kindFilter === "" ? " is-on" : ""}`}
                onClick={() => setKindFilter("")}
              >
                الكل
                <span className="num">{rows.length}</span>
              </button>
              {PAGE_KINDS.map((k) => {
                const n = rows.filter((p) => kindOf(p) === k.key).length;
                return (
                  <button
                    key={k.key}
                    type="button"
                    className={`chip${kindFilter === k.key ? " is-on" : ""}`}
                    onClick={() =>
                      setKindFilter(kindFilter === k.key ? "" : k.key)
                    }
                    style={{ ["--chip-color" as string]: KIND_TONE[k.key] }}
                  >
                    <Icon name={k.icon} size={12} />
                    {k.label.replace("حجز ", "")}
                    <span className="num">{n}</span>
                  </button>
                );
              })}
            </div>
          </div>

          {shown.length === 0 && (
            <p className="mute center mt-4">لا صفحة تطابق البحث.</p>
          )}

          {/*
            بطاقةٌ لكل صفحة لا صفٌّ في جدول.

            الصفحةُ ليست سجلًّا يُقارَن بغيره عمودًا عمودًا: همُّها أن
            يُنسخ رابطُها ويُعرف ما جاء منها. فالرابطُ بارزٌ بزرِّ نسخه،
            والحجوزاتُ رقمٌ يُنقر ليُفتح «من حجز»، والنوعُ لونٌ على الحافة.
          */}
          <div className="bkp-grid">
            {shown.map((p) => {
              const kind = kindOf(p);
              const meta =
                PAGE_KINDS.find((k) => k.key === kind) ?? PAGE_KINDS[0];
              const url = bookingUrl(
                window.location.origin,
                session.tenant.slug,
                p.slug,
              );
              return (
                <article
                  key={p.id}
                  className={`bkp-card${p.isActive ? "" : " is-off"}`}
                  style={{ ["--bkp-tone" as string]: KIND_TONE[kind] }}
                >
                  <header className="bkp-card-head">
                    <span className="bkp-kind">
                      <Icon name={meta.icon} size={13} />
                      {meta.label.replace("حجز ", "")}
                    </span>
                    <Badge kind={p.isActive ? "success" : "neutral"}>
                      {p.isActive ? "تستقبل" : "موقوفة"}
                    </Badge>
                    <Menu
                      label={`إجراءات ${p.name}`}
                      actions={[
                        {
                          key: "copy",
                          label: "انسخ الرابط",
                          icon: "send",
                          onSelect: () => void copy(p),
                        },
                        {
                          key: "open",
                          label: "افتح الصفحة",
                          icon: "calendar",
                          onSelect: () =>
                            window.open(url, "_blank", "noopener"),
                        },
                        {
                          key: "bookings",
                          label: "من حجز",
                          icon: "users",
                          onSelect: () => setViewing(p),
                        },
                        {
                          key: "edit",
                          label: "تعديل",
                          icon: "edit",
                          onSelect: () => setEditing(p),
                        },
                        {
                          key: "toggle",
                          label: p.isActive
                            ? "أوقف الاستقبال"
                            : "استأنف الاستقبال",
                          icon: "refresh",
                          separated: true,
                          onSelect: () => toggle.mutate(p),
                        },
                        {
                          key: "delete",
                          label: "حذف",
                          icon: "trash",
                          danger: true,
                          /*
                            الحذف يُمنع بعد أول حجز.
                            المواعيد تبقى في التقويم، لكن سجلّ «من حجز» يسقط
                            معها — ولا يُستعاد.
                          */
                          disabled: p.bookedCount > 0,
                          title:
                            p.bookedCount > 0
                              ? "عليها حجوزات. أوقف الاستقبال بدل الحذف."
                              : undefined,
                          onSelect: () => remove.mutate(p.id),
                        },
                      ]}
                    />
                  </header>

                  <button
                    type="button"
                    className="bkp-title"
                    onClick={() => setEditing(p)}
                  >
                    {p.name}
                  </button>
                  {p.description && (
                    <p className="tiny mute bkp-desc">{p.description}</p>
                  )}

                  <div className="bkp-meta">
                    <span className="bkp-chip">
                      <Icon name="clock" size={12} />
                      {p.durationMode === "RANGE"
                        ? `حتى ${durationLabel(p.maxDurationMin)}`
                        : durationLabel(p.durationMin)}
                    </span>
                    <span className="bkp-chip">
                      <Icon name="calendar" size={12} />
                      {p.calendarName ?? "—"}
                    </span>
                    {p.ownerName && (
                      <span className="bkp-chip">
                        <Icon name="user" size={12} />
                        {p.ownerName}
                      </span>
                    )}
                    {p.capacity > 1 && (
                      <span className="bkp-chip">
                        <Icon name="users" size={12} />
                        {p.capacity} معًا
                      </span>
                    )}
                  </div>

                  {/* الرابطُ همُّ الشاشة الأول: يُقرأ كاملًا ويُنسخ بنقرة. */}
                  <div className="bkp-link">
                    <a
                      href={url}
                      target="_blank"
                      rel="noopener"
                      className="link num"
                      dir="ltr"
                    >
                      {url}
                    </a>
                    <button
                      type="button"
                      className="btn btn-icon btn-sm"
                      onClick={() => void copy(p)}
                      title="نسخ الرابط"
                      aria-label={`نسخ رابط ${p.name}`}
                    >
                      <Icon name="send" size={13} />
                    </button>
                  </div>

                  <footer className="bkp-foot">
                    <button
                      type="button"
                      className="bkp-count"
                      onClick={() => setViewing(p)}
                      title="من حجز على هذه الصفحة"
                    >
                      <Icon name="users" size={13} />
                      <strong className="num">{p.bookedCount}</strong>
                      <span className="tiny">حجزًا</span>
                    </button>
                    <button
                      type="button"
                      className="btn btn-sm"
                      onClick={() => setEditing(p)}
                    >
                      <Icon name="edit" size={13} />
                      تعديل
                    </button>
                  </footer>
                </article>
              );
            })}
          </div>
        </>
      )}

      {/* ثلاثة أنواعٍ تصف العمل: يُنتقى النوع فيُهيَّأ النموذج له. */}
      {choosing && (
        <Modal title="أيّ صفحةٍ تريد؟" onClose={() => setChoosing(false)}>
          <div className="pick-list">
            {PAGE_KINDS.map((k) => (
              <button
                key={k.key}
                type="button"
                className="pick-row"
                onClick={() => {
                  setChoosing(false);
                  setCreating(k.key);
                }}
              >
                <span className="pick-main">
                  <span className="row" style={{ gap: 6 }}>
                    <Icon name={k.icon} size={14} />
                    <span style={{ fontWeight: 700 }}>{k.label}</span>
                  </span>
                  <span className="tiny mute">{k.hint}</span>
                </span>
                <Icon name="chevronLeft" size={14} />
              </button>
            ))}
          </div>
        </Modal>
      )}

      {(creating || editing) && (
        <PageEditor
          page={editing}
          kind={editing ? kindOf(editing) : creating!}
          onClose={() => {
            setCreating(null);
            setEditing(null);
          }}
          onSaved={() => {
            setCreating(null);
            setEditing(null);
            void queryClient.invalidateQueries({ queryKey: ["booking"] });
          }}
        />
      )}

      {viewing && (
        <BookingsModal page={viewing} onClose={() => setViewing(null)} />
      )}
    </div>
  );
}

/* ==========================================================================
   المحرِّر
   ========================================================================== */

/**
 * ملفٌ إلى `data:` — كما يُخزَّن شعار الشركة وتوقيعها.
 *
 * صفحة الحجز عامّةٌ يفتحها من لا حساب له، ومرفقات النظام خلف مصادقة.
 * فالصورة تُحمل في الصفحة نفسها: تظهر لكل من فتح الرابط بلا خادم ملفات
 * ولا رابطٍ ينكسر — وحدُّها الحجم، ولذلك يُفحص قبل القراءة.
 */
function readAsDataUrl(file: File): Promise<string> {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => resolve(String(reader.result ?? ""));
    reader.onerror = () => reject(reader.error);
    reader.readAsDataURL(file);
  });
}

/** قسمٌ من النموذج: أيقونةٌ ملوّنة وعنوانٌ وتلميح، ثم شبكةُ حقوله. */
function Section({
  icon,
  title,
  hint,
  children,
}: {
  icon: Parameters<typeof Icon>[0]["name"];
  title: string;
  hint?: string;
  children: ReactNode;
}) {
  return (
    <section className="bp-section">
      <header className="bp-section-head">
        <span className="bp-section-icon">
          <Icon name={icon} size={15} />
        </span>
        <span className="bp-section-text">
          <strong>{title}</strong>
          {hint && <span className="tiny mute">{hint}</span>}
        </span>
      </header>
      <div className="form-grid">{children}</div>
    </section>
  );
}

/** خيارٌ بطاقةً — يُضغط فيُعلَّم، وتلميحُه تحت اسمه لا في نافذةٍ عائمة. */
function Choice({
  on,
  icon,
  label,
  hint,
  onClick,
}: {
  on: boolean;
  icon: Parameters<typeof Icon>[0]["name"];
  label: string;
  hint: string;
  onClick: () => void;
}) {
  return (
    <button
      type="button"
      className={`bp-choice${on ? " is-on" : ""}`}
      aria-pressed={on}
      onClick={onClick}
    >
      <span className="bp-choice-icon">
        <Icon name={icon} size={15} />
      </span>
      <span className="bp-choice-text">
        <strong>{label}</strong>
        <span className="tiny mute">{hint}</span>
      </span>
      <span className="bp-choice-check">
        <Icon name="check" size={12} />
      </span>
    </button>
  );
}

/** مفتاحٌ صغير — حالتُه تُقرأ من بعيد. */
function Toggle({
  on,
  onChange,
  label,
  disabled,
}: {
  on: boolean;
  onChange: (v: boolean) => void;
  label: string;
  disabled?: boolean;
}) {
  return (
    <label className={`bp-switch is-inline${disabled ? " is-off" : ""}`}>
      <input
        type="checkbox"
        checked={on}
        disabled={disabled}
        onChange={(e) => onChange(e.target.checked)}
        aria-label={label}
      />
      <span className="bp-switch-track" aria-hidden="true">
        <span className="bp-switch-knob" />
      </span>
    </label>
  );
}

/** رسمٌ خفيف يزيّن صدر النموذج — تقويمٌ مجرَّد بلون النوع. */
function HeroArt() {
  return (
    <svg className="bp-hero-art" viewBox="0 0 120 80" aria-hidden="true">
      <rect
        x="8"
        y="12"
        width="72"
        height="60"
        rx="8"
        fill="currentColor"
        opacity="0.14"
      />
      <rect
        x="8"
        y="12"
        width="72"
        height="16"
        rx="8"
        fill="currentColor"
        opacity="0.35"
      />
      {[0, 1, 2, 3].map((c) =>
        [0, 1, 2].map((r) => (
          <rect
            key={`${c}-${r}`}
            x={16 + c * 16}
            y={36 + r * 11}
            width="10"
            height="7"
            rx="2"
            fill="currentColor"
            opacity={c === 2 && r === 1 ? 0.9 : 0.22}
          />
        )),
      )}
      <circle cx="96" cy="52" r="18" fill="currentColor" opacity="0.18" />
      <path
        d="M88 52 l6 6 l12 -13"
        stroke="currentColor"
        strokeWidth="3.5"
        fill="none"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
    </svg>
  );
}

/* --------------------------------------------------------------------------
   المسودة — ما يُكتب لا يضيع
   -------------------------------------------------------------------------- */

/** كل ما في النموذج — يُحفظ مسودةً ويُستعاد منها. */
interface EditorDraft {
  name: string;
  slug: string;
  description: string;
  calendarId: string;
  ownerId: string;
  durationMin: string;
  bufferMin: string;
  noticeHours: string;
  horizonDays: string;
  createLead: boolean;
  windowStart: string;
  windowEnd: string;
  capacity: number;
  purposes: BookingPurpose[];
  modes: string[];
  locationText: string;
  locationMapsUrl: string;
  priceNote: string;
  cancellationPolicy: string;
  durationMode: string;
  maxDurationMin: string;
  priceUnitMin: string;
  depositPercent: string;
  media: BookingMedia[];
  guestFields: BookingGuestField[];
  extraTerms: BookingExtraTerm[];
  layout: BookingLayout;
  show: BookingShow;
}

/**
 * مفتاحُ المسودة: صفحةٌ قائمة بمعرّفها، وجديدةٌ بنوعها.
 *
 * فمن بدأ «حجز مكان» وخرج ثم عاد إليه وجد ما كتب — وإن عاد إلى
 * «استشارة مجانية» بدأ نظيفًا: المسودتان لا تختلطان.
 */
function draftKeyOf(page: BookingPageView | null, kind: PageKind): string {
  return page
    ? `goyone.bookingDraft.${page.id}`
    : `goyone.bookingDraft.new.${kind}`;
}
function readDraft(key: string): EditorDraft | null {
  try {
    const raw = localStorage.getItem(key);
    return raw ? (JSON.parse(raw) as EditorDraft) : null;
  } catch {
    return null;
  }
}

/* --------------------------------------------------------------------------
   بنّاء نموذج العميل — سحبًا وإفلاتًا
   -------------------------------------------------------------------------- */

/**
 * يبني صاحبُ الصفحة نموذجَ الحجز من عناصر بطاقة العميل.
 *
 * العناصر تُسحب من الرفّ إلى النموذج، وتُرتَّب سحبًا، وتُلزَم بمفتاح.
 * والاسمُ والبريد مقفلان: بهما يُرسَل التأكيد ويُربط العميل — والخادم
 * يضمنهما أيضًا فلا يُترك الأمر للمتصفح. وكل سحبٍ له زرٌّ يفعل فعله
 * لمن لا يسحب: أزرارُ الترتيب والحذف والإضافة.
 */
function GuestFormBuilder({
  fields,
  onChange,
}: {
  fields: BookingGuestField[];
  onChange: (next: BookingGuestField[]) => void;
}) {
  const [over, setOver] = useState<number | null>(null);
  const shelf = GUEST_FIELD_CATALOG.filter(
    (c) => !fields.some((f) => f.key === c.key),
  );
  const lockedKeys: string[] = GUEST_FIELD_CATALOG.filter((c) => c.locked).map(
    (c) => c.key,
  );

  const insertAt = (item: BookingGuestField, at: number) => {
    const next = fields.filter((f) => f.key !== item.key);
    next.splice(Math.min(at, next.length), 0, item);
    onChange(next);
  };
  const move = (from: number, to: number) => {
    if (from === to) return;
    const next = [...fields];
    const [item] = next.splice(from, 1);
    if (!item) return;
    next.splice(to > from ? to - 1 : to, 0, item);
    onChange(next);
  };
  const addKey = (key: string, at = fields.length) => {
    const cat = GUEST_FIELD_CATALOG.find((c) => c.key === key);
    if (!cat) return;
    insertAt({ key: cat.key, label: cat.label, required: cat.locked }, at);
  };
  const addCustom = (at = fields.length) =>
    insertAt(
      {
        key: `q_${Date.now().toString(36)}`,
        label: "سؤال حرّ",
        required: false,
        input: "text",
      },
      at,
    );

  const onDrop = (e: React.DragEvent, at: number) => {
    e.preventDefault();
    setOver(null);
    const data = e.dataTransfer.getData("text/plain");
    if (data.startsWith("add:")) addKey(data.slice(4), at);
    else if (data === "add:custom") addCustom(at);
    else if (data.startsWith("move:")) move(Number(data.slice(5)), at);
  };
  const allowDrop = (e: React.DragEvent, at: number) => {
    e.preventDefault();
    e.dataTransfer.dropEffect = "move";
    if (over !== at) setOver(at);
  };

  return (
    <div className="gfb">
      {/* الرفّ: ما لم يُضَف بعد */}
      <div className="gfb-shelf">
        <span className="tiny mute gfb-shelf-title">
          <Icon name="layers" size={12} /> عناصر بطاقة العميل — اسحبها إلى
          النموذج أو انقر «+»
        </span>
        <div className="gfb-shelf-items">
          {shelf.map((c) => (
            <div
              key={c.key}
              className="gfb-chip"
              draggable
              onDragStart={(e) => {
                e.dataTransfer.setData("text/plain", `add:${c.key}`);
                e.dataTransfer.effectAllowed = "move";
              }}
            >
              <Icon name="menu" size={11} />
              <span>{c.label}</span>
              <button
                type="button"
                className="gfb-chip-add"
                onClick={() => addKey(c.key)}
                aria-label={`أضِف ${c.label}`}
              >
                <Icon name="plus" size={11} />
              </button>
            </div>
          ))}
          <div
            className="gfb-chip is-custom"
            draggable
            onDragStart={(e) => {
              e.dataTransfer.setData("text/plain", "add:custom");
              e.dataTransfer.effectAllowed = "move";
            }}
          >
            <Icon name="menu" size={11} />
            <span>سؤال حرّ</span>
            <button
              type="button"
              className="gfb-chip-add"
              onClick={() => addCustom()}
              aria-label="أضِف سؤالًا حرًّا"
            >
              <Icon name="plus" size={11} />
            </button>
          </div>
        </div>
      </div>

      {/* النموذج كما سيراه العميل — بترتيبه */}
      <ol className="gfb-canvas">
        {fields.map((f, i) => {
          const locked = lockedKeys.includes(f.key);
          const cat = GUEST_FIELD_CATALOG.find((c) => c.key === f.key);
          return (
            <li
              key={f.key}
              className={`gfb-row${over === i ? " is-over" : ""}`}
              draggable
              onDragStart={(e) => {
                e.dataTransfer.setData("text/plain", `move:${i}`);
                e.dataTransfer.effectAllowed = "move";
              }}
              onDragOver={(e) => allowDrop(e, i)}
              onDragLeave={() => setOver((o) => (o === i ? null : o))}
              onDrop={(e) => onDrop(e, i)}
            >
              <span className="gfb-handle" aria-hidden="true">
                <Icon name="menu" size={13} />
              </span>
              <span className="gfb-n num">{i + 1}</span>
              <input
                className="input gfb-label"
                value={f.label}
                onChange={(e) =>
                  onChange(
                    fields.map((x, k) =>
                      k === i ? { ...x, label: e.target.value } : x,
                    ),
                  )
                }
                aria-label="عنوان الحقل"
              />
              {/* السؤال الحرّ يختار نوعَ إجابته — وعناصرُ الفهرس نوعُها ثابت. */}
              {cat ? (
                <span className="tiny mute gfb-kind">
                  {cat.input === "textarea"
                    ? "نصٌّ طويل"
                    : cat.input === "email"
                      ? "بريد"
                      : cat.input === "tel"
                        ? "هاتف"
                        : "نصّ"}
                </span>
              ) : (
                <select
                  className="select gfb-type"
                  value={f.input ?? "text"}
                  onChange={(e) =>
                    onChange(
                      fields.map((x, k) =>
                        k === i
                          ? { ...x, input: e.target.value as GuestInputKind }
                          : x,
                      ),
                    )
                  }
                  aria-label="نوع الإجابة"
                >
                  {GUEST_INPUT_KINDS.map((k) => (
                    <option key={k} value={k}>
                      {GUEST_INPUT_KIND_LABELS[k]}
                    </option>
                  ))}
                </select>
              )}
              <span className="gfb-req">
                <span className="tiny mute">مطلوب</span>
                <Toggle
                  on={f.required}
                  disabled={locked}
                  label={`${f.label} مطلوب`}
                  onChange={(v) =>
                    onChange(
                      fields.map((x, k) =>
                        k === i ? { ...x, required: v } : x,
                      ),
                    )
                  }
                />
              </span>
              <span className="gfb-actions">
                <button
                  type="button"
                  className="btn btn-icon btn-ghost btn-sm"
                  disabled={i === 0}
                  onClick={() => move(i, i - 1)}
                  aria-label="إلى الأعلى"
                >
                  <Icon name="chevronUp" size={12} />
                </button>
                <button
                  type="button"
                  className="btn btn-icon btn-ghost btn-sm"
                  disabled={i === fields.length - 1}
                  onClick={() => move(i, i + 2)}
                  aria-label="إلى الأسفل"
                >
                  <Icon name="chevronDown" size={12} />
                </button>
                <button
                  type="button"
                  className="btn btn-icon btn-ghost btn-sm btn-danger-hover"
                  disabled={locked}
                  title={locked ? "لازمٌ للحجز — لا يُحذف" : "حذف"}
                  onClick={() => onChange(fields.filter((_, k) => k !== i))}
                  aria-label={`حذف ${f.label}`}
                >
                  <Icon name={locked ? "alert" : "trash"} size={12} />
                </button>
              </span>
              {/* الاختيارُ يحتاج خياراتٍ: تُكتب مفصولةً بفواصل وتُحفظ قائمةً. */}
              {!cat &&
                f.input &&
                GUEST_INPUT_WITH_OPTIONS.includes(f.input) && (
                  <label className="gfb-options">
                    <span className="tiny mute nowrap">الخيارات</span>
                    <input
                      className="input"
                      value={(f.options ?? []).join("، ")}
                      onChange={(e) =>
                        onChange(
                          fields.map((x, k) =>
                            k === i
                              ? {
                                  ...x,
                                  options: e.target.value
                                    .split(/[،,]/)
                                    .map((o) => o.trim())
                                    .filter(Boolean)
                                    .slice(0, 30),
                                }
                              : x,
                          ),
                        )
                      }
                      placeholder="صباحًا، ظهرًا، مساءً — مفصولةً بفواصل"
                    />
                  </label>
                )}
            </li>
          );
        })}
        <li
          className={`gfb-drop${over === fields.length ? " is-over" : ""}`}
          onDragOver={(e) => allowDrop(e, fields.length)}
          onDragLeave={() => setOver((o) => (o === fields.length ? null : o))}
          onDrop={(e) => onDrop(e, fields.length)}
        >
          <Icon name="plus" size={12} /> أفلت هنا لتضيف في آخر النموذج
        </li>
      </ol>
    </div>
  );
}

/* --------------------------------------------------------------------------
   الشروط الإضافية — نصٌّ أو نصٌّ بتكلفة
   -------------------------------------------------------------------------- */

function ExtraTermsEditor({
  terms,
  money,
  onChange,
}: {
  terms: BookingExtraTerm[];
  /** الصفحة تتعامل بمال — وإلا فالشرط نصٌّ لا تكلفة له. */
  money: boolean;
  onChange: (next: BookingExtraTerm[]) => void;
}) {
  const kinds = money ? EXTRA_TERM_KINDS : (["TEXT"] as const);
  const set = (i: number, patch: Partial<BookingExtraTerm>) =>
    onChange(terms.map((t, k) => (k === i ? { ...t, ...patch } : t)));
  return (
    <div className="bp-terms">
      {terms.map((t, i) => (
        <div className="bp-term" key={i}>
          <span className="bp-purpose-n num">{i + 1}</span>
          <input
            className="input bp-term-title"
            value={t.title}
            onChange={(e) => set(i, { title: e.target.value })}
            placeholder={
              t.kind === "TEXT"
                ? "يُحضر العميل بطاقة هويته"
                : t.kind === "PERCENT"
                  ? "التأخير عن الموعد أكثر من ساعة"
                  : "الساعة الإضافية بعد المدّة"
            }
            aria-label="نصّ الشرط"
          />
          <select
            className="select bp-term-kind"
            value={t.kind}
            onChange={(e) => {
              const kind = e.target.value as ExtraTermKind;
              set(i, { kind, value: kind === "TEXT" ? null : (t.value ?? 0) });
            }}
            aria-label="نوع الشرط"
          >
            {kinds.map((k) => (
              <option key={k} value={k}>
                {EXTRA_TERM_KIND_LABELS[k]}
              </option>
            ))}
          </select>
          {t.kind !== "TEXT" && (
            <div className="bp-unit bp-term-value">
              <input
                className="input num"
                type="number"
                min={0}
                step="any"
                value={t.value ?? ""}
                onChange={(e) =>
                  set(i, {
                    value:
                      e.target.value === "" ? null : Number(e.target.value),
                  })
                }
                aria-label="قيمة التكلفة"
              />
              <span className="tiny mute nowrap">
                {t.kind === "PERCENT" ? "%" : "ج.م"}
              </span>
            </div>
          )}
          {extraTermCost(t) && (
            <span className="bp-purpose-price num">{extraTermCost(t)}</span>
          )}
          <button
            type="button"
            className="btn btn-sm btn-icon btn-danger-hover"
            onClick={() => onChange(terms.filter((_, k) => k !== i))}
            aria-label="حذف الشرط"
          >
            <Icon name="trash" size={13} />
          </button>
        </div>
      ))}
      <button
        type="button"
        className="btn btn-sm"
        disabled={terms.length >= 20}
        onClick={() =>
          onChange([...terms, { title: "", kind: "TEXT", value: null }])
        }
      >
        <Icon name="plus" size={13} />
        أضِف شرطًا
      </button>
    </div>
  );
}

/* --------------------------------------------------------------------------
   المعاينة الحيّة — الصفحة نفسها داخل جهاز
   -------------------------------------------------------------------------- */

type Device = "mobile" | "desktop";
/* الإطار (bezel) جزءٌ من الجهاز: يُحسب في القياس وإلا فاض عن المسرح. */
const DEVICES: Readonly<
  Record<
    Device,
    {
      w: number;
      h: number;
      bx: number;
      by: number;
      label: string;
      icon: "phone" | "expand";
    }
  >
> = {
  mobile: { w: 375, h: 812, bx: 20, by: 20, label: "موبايل", icon: "phone" },
  desktop: {
    w: 1280,
    h: 800,
    bx: 16,
    by: 34,
    label: "ديسكتوب",
    icon: "expand",
  },
};

/**
 * أوقاتٌ توضيحية لصفحةٍ لم تُحفظ بعد.
 *
 * الأوقات الحقيقية يحسبها الخادم من التقويم والمحجوز، وصفحةٌ بلا معرّف
 * لا أوقات لها. فتُرسم هنا خمسةُ أيامٍ من ساعات النافذة — لتُرى الشبكة
 * كما ستبدو — ويقول شريطُ المعاينة إنها توضيحية.
 */
function demoDays(opts: {
  durationMin: number;
  bufferMin: number;
  windowStart: string;
  windowEnd: string;
}): BookingDay[] {
  const [sh, sm] = (opts.windowStart || "09:00").split(":").map(Number);
  const [eh, em] = (opts.windowEnd || "17:00").split(":").map(Number);
  const step = Math.max(5, opts.durationMin + opts.bufferMin);
  const days: BookingDay[] = [];
  for (let d = 1; d <= 5; d++) {
    const day = new Date();
    day.setDate(day.getDate() + d);
    day.setHours(sh ?? 9, sm ?? 0, 0, 0);
    const end = new Date(day);
    end.setHours(eh ?? 17, em ?? 0, 0, 0);
    const slots: BookingDay["slots"] = [];
    for (
      let t = new Date(day);
      t.getTime() + opts.durationMin * 60_000 <= end.getTime() &&
      slots.length < 12;
      t = new Date(t.getTime() + step * 60_000)
    ) {
      slots.push({
        startAt: t.toISOString(),
        endAt: new Date(t.getTime() + opts.durationMin * 60_000).toISOString(),
      });
    }
    if (slots.length)
      days.push({ date: day.toISOString().slice(0, 10), slots });
  }
  return days;
}

function LivePreview({
  device,
  onDevice,
  onClose,
  info,
  days,
  demo,
}: {
  device: Device;
  onDevice: (d: Device) => void;
  onClose: () => void;
  info: PublicBookingPage;
  days: BookingDay[];
  /** الأوقات توضيحية لا من التقويم. */
  demo: boolean;
}) {
  const frame = useRef<HTMLIFrameElement>(null);
  const [scale, setScale] = useState(1);
  const dev = DEVICES[device];

  /* الجهاز يُصغَّر ليدخل الشاشة — والمقاسُ الحقيقي يبقى داخل الإطار. */
  useEffect(() => {
    const fit = () => {
      const w = window.innerWidth - 48;
      const h = window.innerHeight - 132;
      setScale(Math.min(1, w / (dev.w + dev.bx), h / (dev.h + dev.by)));
    };
    fit();
    window.addEventListener("resize", fit);
    return () => window.removeEventListener("resize", fit);
  }, [dev.w, dev.h, dev.bx, dev.by]);

  /* البيانات تُبعث عند جاهزيّة الإطار ومع كل تغيير — فالصفحة تتغيّر تحت العين. */
  const send = () => {
    frame.current?.contentWindow?.postMessage(
      { type: "goyone.bookingPreview", info, days },
      window.location.origin,
    );
  };
  useEffect(() => {
    const onMessage = (e: MessageEvent) => {
      if (e.origin !== window.location.origin) return;
      if ((e.data as { type?: string })?.type === "goyone.bookingPreviewReady")
        send();
    };
    window.addEventListener("message", onMessage);
    return () => window.removeEventListener("message", onMessage);
  });
  useEffect(() => {
    send();
  });

  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (e.key === "Escape") onClose();
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [onClose]);

  return createPortal(
    <div className="bp-live" role="dialog" aria-label="معاينة صفحة الحجز">
      <div className="bp-live-bar">
        <span className="bp-live-title">
          <Icon name="eye" size={14} /> كما يراها العميل
          <span className="tiny mute">
            {demo
              ? " — الأوقات توضيحية حتى تُحفظ الصفحة"
              : " — الأوقات من التقويم"}
          </span>
        </span>
        <div className="bp-live-devices" role="group" aria-label="الجهاز">
          {(Object.keys(DEVICES) as Device[]).map((d) => (
            <button
              key={d}
              type="button"
              className={`btn btn-sm${device === d ? " btn-primary" : ""}`}
              aria-pressed={device === d}
              onClick={() => onDevice(d)}
            >
              <Icon name={DEVICES[d].icon} size={13} />
              {DEVICES[d].label}
              <span className="tiny num" dir="ltr">
                {DEVICES[d].w}×{DEVICES[d].h}
              </span>
            </button>
          ))}
        </div>
        <button
          type="button"
          className="btn btn-sm btn-icon"
          onClick={onClose}
          aria-label="إغلاق المعاينة"
        >
          <Icon name="x" size={14} />
        </button>
      </div>
      <div className="bp-live-stage">
        <div
          className="bp-live-box"
          style={{
            width: (dev.w + dev.bx) * scale,
            height: (dev.h + dev.by) * scale,
          }}
        >
          <div
            className={`bp-live-device is-${device}`}
            style={{
              width: dev.w,
              height: dev.h,
              transform: `scale(${scale})`,
            }}
          >
            <iframe
              ref={frame}
              title="معاينة صفحة الحجز"
              src="/book/preview"
              onLoad={send}
              style={{ width: dev.w, height: dev.h }}
            />
          </div>
        </div>
        <span className="tiny mute bp-live-scale num" dir="ltr">
          {Math.round(scale * 100)}%
        </span>
      </div>
    </div>,
    document.body,
  );
}

/* --------------------------------------------------------------------------
   المحرِّر
   -------------------------------------------------------------------------- */

/* --------------------------------------------------------------------------
   محرّر صفحة العرض — عنوانٌ وأرقامٌ وفصولٌ وأسئلة
   -------------------------------------------------------------------------- */

/**
 * محتوى صفحة العرض يُكتب هنا فصلًا فصلًا.
 *
 * الصفحة العادية تُبنى مما في الصفحة أصلًا — الاسمُ والوصفُ والأغراض.
 * أما العرضُ فيحتاج ما لا تعرفه الصفحة: عنوانًا يُقرأ من بعيد، وأرقامًا
 * تُقنع، وفصولًا لكلٍّ عنوانُه ونصُّه وصورتُه ونقاطُه، وأسئلةً تُجاب قبل
 * أن تُسأل. وكلُّه يُرى في المعاينة الحيّة وهو يُكتب.
 */
function ShowEditor({
  value,
  onChange,
}: {
  value: BookingShow;
  onChange: (v: BookingShow) => void;
}) {
  const toast = useToast();
  const set = (patch: Partial<BookingShow>) => onChange({ ...value, ...patch });
  const setChapter = (i: number, patch: Partial<BookingShowChapter>) =>
    set({
      chapters: value.chapters.map((c, k) =>
        k === i ? { ...c, ...patch } : c,
      ),
    });
  const moveChapter = (from: number, to: number) => {
    if (to < 0 || to >= value.chapters.length) return;
    const next = [...value.chapters];
    const [c] = next.splice(from, 1);
    if (!c) return;
    next.splice(to, 0, c);
    set({ chapters: next });
  };

  return (
    <div className="se">
      {/* السمة */}
      <div className="field">
        <span className="label">سمة الصفحة</span>
        <div className="bp-choices">
          <Choice
            on={value.theme === "dark"}
            icon="star"
            label="داكنة"
            hint="خلفيةٌ ليليّة وعنوانٌ أبيض — كإصدارات المنتجات."
            onClick={() => set({ theme: "dark" })}
          />
          <Choice
            on={value.theme === "light"}
            icon="expand"
            label="فاتحة"
            hint="خلفيةٌ بيضاء وعناوين داكنة."
            onClick={() => set({ theme: "light" })}
          />
        </div>
      </div>

      {/* البطل */}
      <div className="se-group">
        <span className="se-group-title">
          <Icon name="rocket" size={13} /> صدرُ الصفحة
        </span>
        <div className="form-grid">
          <label className="field">
            <span className="label">سطرٌ فوق العنوان</span>
            <input
              className="input"
              value={value.kicker}
              onChange={(e) => set({ kicker: e.target.value })}
              placeholder="إصدار الشتاء ٢٠٢٦"
            />
          </label>
          <label className="field">
            <span className="label">نصّ زرّ الحجز</span>
            <input
              className="input"
              value={value.ctaLabel}
              onChange={(e) => set({ ctaLabel: e.target.value })}
              placeholder="احجز الآن"
            />
          </label>
          <label className="field span-2">
            <span className="label">العنوان الكبير</span>
            <input
              className="input"
              value={value.headline}
              onChange={(e) => set({ headline: e.target.value })}
              placeholder="فارغًا يأخذ اسم الصفحة — «استوديو بحكاية»"
            />
          </label>
          <label className="field span-2">
            <span className="label">سطرٌ تحت العنوان</span>
            <textarea
              className="textarea"
              rows={2}
              value={value.subheadline}
              onChange={(e) => set({ subheadline: e.target.value })}
              placeholder="عالمٌ جديد للتصوير — ١٥٠ مترًا من الإضاءة والخلفيات والهدوء."
            />
          </label>
        </div>
      </div>

      {/* الأرقام */}
      <div className="se-group">
        <span className="se-group-title">
          <Icon name="chartBar" size={13} /> أرقامٌ بارزة
          <span className="tiny mute">
            — «+١٥٠ عميلًا»، «٢٠٠ متر»، «١٢ خلفية»
          </span>
        </span>
        <div className="se-rows">
          {value.stats.map((st, i) => (
            <div className="se-row" key={i}>
              <input
                className="input num"
                style={{ flex: 1, minWidth: "6rem" }}
                value={st.value}
                onChange={(e) =>
                  set({
                    stats: value.stats.map((x, k) =>
                      k === i ? { ...x, value: e.target.value } : x,
                    ),
                  })
                }
                placeholder="+150"
              />
              <input
                className="input"
                style={{ flex: 2, minWidth: "9rem" }}
                value={st.label}
                onChange={(e) =>
                  set({
                    stats: value.stats.map((x, k) =>
                      k === i ? { ...x, label: e.target.value } : x,
                    ),
                  })
                }
                placeholder="عميلًا صوّرنا لهم"
              />
              <button
                type="button"
                className="btn btn-sm btn-icon btn-danger-hover"
                onClick={() =>
                  set({ stats: value.stats.filter((_, k) => k !== i) })
                }
                aria-label="حذف الرقم"
              >
                <Icon name="trash" size={13} />
              </button>
            </div>
          ))}
          <button
            type="button"
            className="btn btn-sm"
            disabled={value.stats.length >= 6}
            onClick={() =>
              set({ stats: [...value.stats, { value: "", label: "" }] })
            }
          >
            <Icon name="plus" size={13} /> أضِف رقمًا
          </button>
        </div>
      </div>

      {/* الفصول */}
      <div className="se-group">
        <span className="se-group-title">
          <Icon name="book" size={13} /> الفصول
          <span className="tiny mute">
            — كلُّ فصلٍ عنوانٌ ونصٌّ وصورةٌ ونقاط، وتتبادل الجهة تلقائيًّا
          </span>
        </span>
        <div className="se-chapters">
          {value.chapters.map((c, i) => (
            <div className="se-chapter" key={c.id}>
              <div className="se-chapter-head">
                <span className="bp-purpose-n num">{i + 1}</span>
                <input
                  className="input se-chapter-title"
                  value={c.title}
                  onChange={(e) => setChapter(i, { title: e.target.value })}
                  placeholder="عنوان الفصل — الإضاءة"
                />
                <span className="gfb-actions">
                  <button
                    type="button"
                    className="btn btn-icon btn-ghost btn-sm"
                    disabled={i === 0}
                    onClick={() => moveChapter(i, i - 1)}
                    aria-label="إلى الأعلى"
                  >
                    <Icon name="chevronUp" size={12} />
                  </button>
                  <button
                    type="button"
                    className="btn btn-icon btn-ghost btn-sm"
                    disabled={i === value.chapters.length - 1}
                    onClick={() => moveChapter(i, i + 1)}
                    aria-label="إلى الأسفل"
                  >
                    <Icon name="chevronDown" size={12} />
                  </button>
                  <button
                    type="button"
                    className="btn btn-icon btn-ghost btn-sm btn-danger-hover"
                    onClick={() =>
                      set({
                        chapters: value.chapters.filter((_, k) => k !== i),
                      })
                    }
                    aria-label="حذف الفصل"
                  >
                    <Icon name="trash" size={12} />
                  </button>
                </span>
              </div>
              <div className="form-grid">
                <label className="field">
                  <span className="label">سطرٌ فوق العنوان</span>
                  <input
                    className="input"
                    value={c.kicker}
                    onChange={(e) => setChapter(i, { kicker: e.target.value })}
                    placeholder="الفصل الأول"
                  />
                </label>
                <div className="field">
                  <span className="label">صورةٌ أو فيديو</span>
                  {c.media ? (
                    <div className="se-media">
                      {c.media.kind === "IMAGE" ? (
                        <img src={c.media.url} alt="" />
                      ) : (
                        <span className="bp-media-video">
                          <Icon name="video" size={16} />
                        </span>
                      )}
                      <button
                        type="button"
                        className="btn btn-sm"
                        onClick={() => setChapter(i, { media: null })}
                      >
                        <Icon name="x" size={12} /> إزالة
                      </button>
                    </div>
                  ) : (
                    <div className="bp-add">
                      <label className="bp-upload">
                        <Icon name="plus" size={13} />
                        <span>ارفع صورة</span>
                        <input
                          type="file"
                          accept="image/*"
                          onChange={async (e) => {
                            const file = e.target.files?.[0];
                            e.target.value = "";
                            if (!file) return;
                            if (file.size > 900_000) {
                              toast(
                                "الصورة أكبر من ٩٠٠ كيلوبايت — اضغطها أو اختر أصغر.",
                              );
                              return;
                            }
                            const url = await readAsDataUrl(file);
                            setChapter(i, {
                              media: { kind: "IMAGE", url, caption: null },
                            });
                          }}
                        />
                      </label>
                      <input
                        className="input num"
                        dir="ltr"
                        style={{ flex: 1, minWidth: "9rem" }}
                        placeholder="أو رابط فيديو"
                        onKeyDown={(e) => {
                          const v = (e.target as HTMLInputElement).value.trim();
                          if (e.key === "Enter" && v) {
                            e.preventDefault();
                            setChapter(i, {
                              media: { kind: "VIDEO", url: v, caption: null },
                            });
                          }
                        }}
                        onBlur={(e) => {
                          const v = e.target.value.trim();
                          if (v)
                            setChapter(i, {
                              media: { kind: "VIDEO", url: v, caption: null },
                            });
                        }}
                      />
                    </div>
                  )}
                </div>
                <label className="field span-2">
                  <span className="label">نصّ الفصل</span>
                  <textarea
                    className="textarea"
                    rows={3}
                    value={c.body}
                    onChange={(e) => setChapter(i, { body: e.target.value })}
                    placeholder="إضاءةٌ من ثلاث جهات وخلفياتٌ تُبدَّل في دقيقة — فتصوّر منتجك كما يستحقّ."
                  />
                </label>
                <label className="field span-2">
                  <span className="label">نقاطٌ مختصرة — سطرٌ لكلّ نقطة</span>
                  <textarea
                    className="textarea"
                    rows={3}
                    value={c.bullets.join("\n")}
                    onChange={(e) =>
                      setChapter(i, {
                        bullets: e.target.value
                          .split("\n")
                          .map((b) => b.trimEnd())
                          .slice(0, 8),
                      })
                    }
                    placeholder={
                      "إضاءة LED قابلة للتعديل\nخلفيات ورقية ١٢ لونًا\nغرفة تبديل ملابس"
                    }
                  />
                </label>
              </div>
            </div>
          ))}
          <button
            type="button"
            className="btn btn-sm"
            disabled={value.chapters.length >= 12}
            onClick={() =>
              set({
                chapters: [
                  ...value.chapters,
                  {
                    id: `ch_${Date.now().toString(36)}`,
                    kicker: "",
                    title: "",
                    body: "",
                    media: null,
                    bullets: [],
                  },
                ],
              })
            }
          >
            <Icon name="plus" size={13} /> أضِف فصلًا
          </button>
        </div>
      </div>

      {/* الأسئلة */}
      <div className="se-group">
        <span className="se-group-title">
          <Icon name="info" size={13} /> أسئلة شائعة
        </span>
        <div className="se-rows">
          {value.faq.map((f, i) => (
            <div className="se-faq" key={i}>
              <input
                className="input"
                value={f.q}
                onChange={(e) =>
                  set({
                    faq: value.faq.map((x, k) =>
                      k === i ? { ...x, q: e.target.value } : x,
                    ),
                  })
                }
                placeholder="هل يمكن إحضار مصوّري الخاصّ؟"
              />
              <textarea
                className="textarea"
                rows={2}
                value={f.a}
                onChange={(e) =>
                  set({
                    faq: value.faq.map((x, k) =>
                      k === i ? { ...x, a: e.target.value } : x,
                    ),
                  })
                }
                placeholder="نعم — المكانُ والمعدّات لك، ومن يصوّر شأنُك."
              />
              <button
                type="button"
                className="btn btn-sm btn-icon btn-danger-hover"
                onClick={() =>
                  set({ faq: value.faq.filter((_, k) => k !== i) })
                }
                aria-label="حذف السؤال"
              >
                <Icon name="trash" size={13} />
              </button>
            </div>
          ))}
          <button
            type="button"
            className="btn btn-sm"
            disabled={value.faq.length >= 12}
            onClick={() => set({ faq: [...value.faq, { q: "", a: "" }] })}
          >
            <Icon name="plus" size={13} /> أضِف سؤالًا
          </button>
        </div>
      </div>
    </div>
  );
}

/**
 * غلافٌ يملك «عهد» المحرّر: تجاهلُ المسودة يُعيد فتحه نظيفًا.
 *
 * المسودة تُقرأ مرّةً عند الفتح فتبدأ الحقول منها — أسهل من ثلاثين
 * مُعيِّنًا تُنادى. ومن أراد التخلّي عنها يُعاد له المحرّر بمفتاحٍ جديد
 * فيبدأ من الصفحة كما هي.
 */
function PageEditor(props: {
  page: BookingPageView | null;
  kind: PageKind;
  onClose: () => void;
  onSaved: () => void;
}) {
  const [epoch, setEpoch] = useState(0);
  return (
    <PageEditorInner
      key={epoch}
      {...props}
      onDiscardDraft={() => {
        try {
          localStorage.removeItem(draftKeyOf(props.page, props.kind));
        } catch {
          /* لا تخزين — لا مسودة أصلًا. */
        }
        setEpoch((e) => e + 1);
      }}
    />
  );
}

function PageEditorInner({
  page,
  kind,
  onClose,
  onSaved,
  onDiscardDraft,
}: {
  page: BookingPageView | null;
  /** نوع الصفحة — به تُهيَّأ الحقول ويُخفى ما لا يخصّه. */
  kind: PageKind;
  onClose: () => void;
  onSaved: () => void;
  onDiscardDraft: () => void;
}) {
  /* المال يخصّ المدفوع وحده، والمكان والمدى يخصّان حجز المكان. */
  const money = kind !== "FREE_CONSULT";
  const kindMeta = PAGE_KINDS.find((k) => k.key === kind) ?? PAGE_KINDS[0];
  const kindLabel = kindMeta.label;
  const place = kind === "PLACE";
  const toast = useToast();
  const session = useSession();
  const profile = useTenantProfile();
  const { assignable } = useTeamDirectory();

  /* المسودة تُقرأ مرّةً — وتبدأ منها الحقول إن وُجدت. */
  const draftKey = draftKeyOf(page, kind);
  const draft = useMemo(() => readDraft(draftKey), [draftKey]);
  const [draftRestored] = useState(Boolean(draft));

  const [name, setName] = useState(draft?.name ?? page?.name ?? "");
  const [slug, setSlug] = useState(draft?.slug ?? page?.slug ?? "");
  const [description, setDescription] = useState(
    draft?.description ?? page?.description ?? "",
  );
  const [calendarId, setCalendarId] = useState(
    draft?.calendarId ?? page?.calendarId ?? "",
  );
  const [ownerId, setOwnerId] = useState(draft?.ownerId ?? page?.ownerId ?? "");
  const [durationMin, setDurationMin] = useState(
    draft?.durationMin ?? String(page?.durationMin ?? (place ? 60 : 30)),
  );
  const [bufferMin, setBufferMin] = useState(
    draft?.bufferMin ?? String(page?.bufferMin ?? 0),
  );
  const [noticeHours, setNoticeHours] = useState(
    draft?.noticeHours ?? String(page?.noticeHours ?? 4),
  );
  const [horizonDays, setHorizonDays] = useState(
    draft?.horizonDays ?? String(page?.horizonDays ?? 30),
  );
  const [createLead, setCreateLead] = useState(
    draft?.createLead ?? page?.createLead ?? true,
  );
  const [windowStart, setWindowStart] = useState(
    draft?.windowStart ?? page?.windowStart ?? "",
  );
  const [windowEnd, setWindowEnd] = useState(
    draft?.windowEnd ?? page?.windowEnd ?? "",
  );
  /*
   * كم حجزًا يقع في الوقت نفسه.
   *
   * قاعةٌ تتّسع لثلاث جلساتٍ معًا، ومستشارٌ واحدٌ لا يجلس مع اثنين.
   */
  const [capacity, setCapacity] = useState(
    draft?.capacity ?? page?.capacity ?? 1,
  );
  /* الأغراض صفوفٌ: اسمٌ وسعرٌ — والسعر يتبع ما يُطلب لا الصفحةَ كلَّها. */
  const [purposes, setPurposes] = useState<BookingPurpose[]>(
    draft?.purposes ?? page?.purposes ?? [],
  );
  const [purposeName, setPurposeName] = useState("");
  const [purposePrice, setPurposePrice] = useState("");
  const [modes, setModes] = useState<string[]>(
    draft?.modes ?? page?.modes ?? (place ? ["ONSITE"] : ["ONLINE"]),
  );
  const [locationText, setLocationText] = useState(
    draft?.locationText ?? page?.locationText ?? "",
  );
  const [locationMapsUrl, setLocationMapsUrl] = useState(
    draft?.locationMapsUrl ?? page?.locationMapsUrl ?? "",
  );
  const [priceNote, setPriceNote] = useState(
    draft?.priceNote ?? page?.priceNote ?? "",
  );
  const [cancellationPolicy, setCancellationPolicy] = useState(
    draft?.cancellationPolicy ?? page?.cancellationPolicy ?? "",
  );
  const [durationMode, setDurationMode] = useState(
    draft?.durationMode ?? page?.durationMode ?? (place ? "RANGE" : "FIXED"),
  );
  const [maxDurationMin, setMaxDurationMin] = useState(
    draft?.maxDurationMin ?? String(page?.maxDurationMin ?? 240),
  );
  const [priceUnitMin, setPriceUnitMin] = useState(
    draft?.priceUnitMin ?? String(page?.priceUnitMin ?? 60),
  );
  const [depositPercent, setDepositPercent] = useState(
    draft?.depositPercent ?? String(page?.depositPercent ?? 0),
  );
  /* معرض الصفحة — صورٌ تُرفع وفيديو برابطه، تُضاف واحدةً واحدة. */
  const [media, setMedia] = useState<BookingMedia[]>(
    draft?.media ?? page?.media ?? [],
  );
  const [mediaUrl, setMediaUrl] = useState("");
  const [uploading, setUploading] = useState(false);
  const [mediaCaption, setMediaCaption] = useState("");
  /* نموذج العميل والشروط الإضافية — يبنيهما صاحب الصفحة. */
  const [guestFields, setGuestFields] = useState<BookingGuestField[]>(
    draft?.guestFields ??
      (page?.guestFields?.length
        ? page.guestFields
        : [...DEFAULT_GUEST_FIELDS]),
  );
  const [extraTerms, setExtraTerms] = useState<BookingExtraTerm[]>(
    draft?.extraTerms ?? page?.extraTerms ?? [],
  );
  /* شكلُ الصفحة ومحتوى العرض — يُكتبان هنا ويُريان في المعاينة. */
  const [layout, setLayout] = useState<BookingLayout>(
    draft?.layout ?? page?.layout ?? "CLASSIC",
  );
  const [show, setShow] = useState<BookingShow>(
    draft?.show ?? page?.show ?? DEFAULT_BOOKING_SHOW,
  );
  const [error, setError] = useState<string | null>(null);
  const [live, setLive] = useState<Device | null>(null);
  /*
   * الصدرُ ثابتٌ والتمريرُ من بعده.
   *
   * يلتصق الصدر بأعلى النافذة فيبقى النوعُ والاسمُ في العين وأنت في آخر
   * الأقسام. وارتفاعُه يُقاس ليُحسب منه التصاقُ المعاينة الجانبية تحته —
   * لا رقمٌ ثابت يخالفه سطرٌ زائد في التلميح.
   */
  const root = useRef<HTMLDivElement>(null);
  const hero = useRef<HTMLDivElement>(null);
  useEffect(() => {
    const el = hero.current;
    const box = root.current;
    if (!el || !box) return;
    /*
     * العمود الجانبي يلتصق حيث يبدأ — فلا يتحرّك مع التمرير.
     *
     * التصاقٌ عند نقطةٍ غير نقطة البداية يعني أن البطاقة تصعد أولًا ثم
     * تقف: حركةٌ تُرى. فيُقاس موضعُها الطبيعي من أعلى صندوق التمرير
     * ويُجعل هو نقطةَ الالتصاق — ويُعاد القياس حين يتغيّر ما فوقها.
     */
    const apply = () => {
      box.style.setProperty("--bp-hero-h", `${el.offsetHeight}px`);
      const scroller = box.closest(".modal-body") as HTMLElement | null;
      const layout = box.querySelector(".bp-layout") as HTMLElement | null;
      if (scroller && layout) {
        const top =
          layout.getBoundingClientRect().top -
          scroller.getBoundingClientRect().top +
          scroller.scrollTop;
        box.style.setProperty("--bp-aside-top", `${Math.round(top)}px`);
      }
    };
    apply();
    const ro = new ResizeObserver(apply);
    ro.observe(el);
    ro.observe(box);
    return () => ro.disconnect();
  }, []);

  /*
   * كتالوج المبيعات — الخدمات والباقات — يُنتقى منه الغرض.
   *
   * ما يُنتقى يحمل معرّفَه وسعرَه ووحدتَه، فيصير عرضُ السعر من الحجز
   * ببنودٍ من الكتالوج نفسه لا بوصفٍ مكتوب. والباقةُ سعرُها مجموعُ
   * بنودها المفكوكة بخدمة الباقات.
   */
  const catalogServices = useQuery({
    queryKey: ["services", "options"],
    queryFn: () =>
      api<{
        items: Array<{
          id: string;
          name: string;
          unit: string;
          unitPrice: number | string;
        }>;
      }>("/sales/services?pageSize=100&isActive=true"),
  });
  const catalogPackages = useQuery({
    queryKey: ["packages", "options"],
    queryFn: () =>
      api<{ items: Array<{ id: string; name: string }> }>(
        "/sales/packages?pageSize=100&isActive=true",
      ),
  });
  const addFromCatalog = async (value: string) => {
    const [kind, id] = value.split(":");
    if (kind === "s") {
      const svc = (catalogServices.data?.items ?? []).find((x) => x.id === id);
      if (!svc) return;
      setPurposes((prev) => [
        ...prev,
        {
          name: svc.name,
          price: money ? Number(svc.unitPrice) : null,
          serviceId: svc.id,
          unit: svc.unit,
        },
      ]);
      return;
    }
    const pkg = (catalogPackages.data?.items ?? []).find((x) => x.id === id);
    if (!pkg) return;
    try {
      const r = await api<{
        lines: Array<{ quantity: number; unitPrice: number; discount: number }>;
      }>(`/sales/packages/${id}/expand`, {
        method: "POST",
        body: { quantity: 1 },
      });
      const total = r.lines.reduce(
        (t, l) => t + l.quantity * l.unitPrice - (l.discount || 0),
        0,
      );
      setPurposes((prev) => [
        ...prev,
        {
          name: pkg.name,
          price: money ? Math.round(total) : null,
          packageId: pkg.id,
          unit: "باقة",
        },
      ]);
    } catch (e) {
      toast(e instanceof Error ? e.message : "تعذّر قراءة الباقة.");
    }
  };

  const calendars = useQuery({
    queryKey: ["calendars"],
    queryFn: () => api<CalendarRow[]>("/calendar/calendars"),
  });

  /*
   * المسودة تُكتب مع كل تغيير — وتُمحى حين لا فرق.
   *
   * ما يُكتب لا يضيع: من أغلق النافذة خطأً أو انقطع به الاتصال وجد ما
   * كتبه حين يعود. ولا تُكتب مسودةٌ لنموذجٍ لم يُمسّ — وإلا لاستُعيدت
   * «مسودة» فارغة عند كل فتح. والصورُ قد تتجاوز سعة التخزين، فتُحفظ
   * المسودة بلا صورها خيرًا من أن تضيع كلُّها.
   */
  const snapshot: EditorDraft = {
    name,
    slug,
    description,
    calendarId,
    ownerId,
    durationMin,
    bufferMin,
    noticeHours,
    horizonDays,
    createLead,
    windowStart,
    windowEnd,
    capacity,
    purposes,
    modes,
    locationText,
    locationMapsUrl,
    priceNote,
    cancellationPolicy,
    durationMode,
    maxDurationMin,
    priceUnitMin,
    depositPercent,
    media,
    guestFields,
    extraTerms,
    layout,
    show,
  };
  const snapshotJson = JSON.stringify(snapshot);
  const initialJson = useRef<string | null>(null);
  if (initialJson.current === null && !draft)
    initialJson.current = snapshotJson;
  useEffect(() => {
    const t = setTimeout(() => {
      try {
        if (
          initialJson.current !== null &&
          snapshotJson === initialJson.current
        ) {
          localStorage.removeItem(draftKey);
          return;
        }
        try {
          localStorage.setItem(draftKey, snapshotJson);
        } catch {
          localStorage.setItem(
            draftKey,
            JSON.stringify({ ...snapshot, media: [] }),
          );
        }
      } catch {
        /* لا تخزين في هذا المتصفح — يعمل النموذج بلا مسودة. */
      }
    }, 400);
    return () => clearTimeout(t);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [snapshotJson, draftKey]);

  const save = useMutation({
    mutationFn: () => {
      const body = {
        name: name.trim(),
        slug: slug.trim(),
        description: description.trim() || null,
        calendarId,
        ownerId: ownerId || null,
        durationMin: Number(durationMin),
        bufferMin: Number(bufferMin) || 0,
        noticeHours: Number(noticeHours) || 0,
        windowStart: windowStart || null,
        windowEnd: windowEnd || null,
        horizonDays: Number(horizonDays) || 30,
        capacity,
        kind,
        purposes,
        modes,
        locationText: locationText.trim() || null,
        locationMapsUrl: locationMapsUrl.trim() || null,
        /* الصفحة بلا سعر: كل غرضٍ بسعره. */
        price: null,
        priceNote: priceNote.trim() || null,
        cancellationPolicy: cancellationPolicy.trim() || null,
        durationMode,
        maxDurationMin: Number(maxDurationMin) || 240,
        priceUnitMin: Number(priceUnitMin) || 60,
        depositPercent: Number(depositPercent) || 0,
        media,
        layout,
        show,
        guestFields: guestFields.map((f) => ({
          ...f,
          label: f.label.trim() || f.key,
        })),
        extraTerms: extraTerms
          .filter((t) => t.title.trim())
          .map((t) => ({
            ...t,
            title: t.title.trim(),
            value: t.kind === "TEXT" ? null : t.value,
          })),
        createLead,
      };
      return page
        ? api(`/booking/pages/${page.id}`, { method: "PATCH", body })
        : api("/booking/pages", { method: "POST", body });
    },
    onSuccess: () => {
      try {
        localStorage.removeItem(draftKey);
      } catch {
        /* لا تخزين. */
      }
      toast(page ? "حُدّثت الصفحة" : "أُنشئت الصفحة");
      onSaved();
    },
    onError: (e) => setError(e instanceof Error ? e.message : "تعذّر الحفظ."),
  });

  const usable = (calendars.data ?? []).filter((c) => c.isActive);
  const calendar = usable.find((c) => c.id === calendarId);

  /* ما ينقص قبل الحفظ — يُسمّى بدل زرٍّ معطَّلٍ بلا سبب. */
  const missing = [
    !name.trim() && "الاسم",
    !slug.trim() && "الرابط",
    !calendarId && "التقويم",
  ].filter((x): x is string => Boolean(x));

  /* مدى أسعار الأغراض — كما سيراه العميل في صدر صفحته. */
  const prices = purposes
    .map((x) => x.price)
    .filter((x): x is number => x != null);
  const priceRange =
    prices.length === 0
      ? null
      : Math.min(...prices) === Math.max(...prices)
        ? `${Math.min(...prices).toLocaleString("en-US")} ج.م`
        : `${Math.min(...prices).toLocaleString("en-US")}–${Math.max(...prices).toLocaleString("en-US")} ج.م`;

  const cover = media.find((m) => m.kind === "IMAGE");

  /*
   * الصفحة كما سيراها العميل — تُبنى من الحقول لا تُجلب.
   *
   * هي الحمولةُ نفسها التي يردّها الخادم للمسار العامّ، مصنوعةً هنا من
   * حال النموذج: فالمعاينة لا تحتاج حفظًا ولا رابطًا، وتتغيّر مع كل حرف.
   */
  const previewInfo: PublicBookingPage = {
    slug: slug.trim() || "preview",
    name: name.trim() || "اسم الصفحة",
    description: description.trim() || null,
    tenantName: profile.data?.tenant.name ?? session.tenant.slug,
    tenantPhone: profile.data?.company.phone || null,
    tenantEmail: profile.data?.company.email || null,
    tenantAddress: profile.data?.company.address || null,
    defaultCountryCode: profile.data?.settings.defaultCountryCode || "20",
    logoUrl: profile.data?.branding.logoUrl || null,
    brandColor: profile.data?.branding.primary || null,
    hostName: place
      ? null
      : (assignable.find((m) => m.userId === ownerId)?.name ?? null),
    durationMin: Number(durationMin) || 30,
    noticeHours: Number(noticeHours) || 0,
    horizonDays: Number(horizonDays) || 30,
    purposes,
    modes,
    locationText: locationText.trim() || null,
    locationMapsUrl: locationMapsUrl.trim() || null,
    price: null,
    priceNote: money ? priceNote.trim() || null : priceNote.trim() || null,
    cancellationPolicy: cancellationPolicy.trim() || null,
    guestFields,
    extraTerms: extraTerms.filter((t) => t.title.trim()),
    durationMode,
    maxDurationMin: Number(maxDurationMin) || 240,
    capacity,
    kind,
    priceUnitMin: Number(priceUnitMin) || 60,
    depositPercent: Number(depositPercent) || 0,
    media,
    layout,
    show,
  };

  /* الأوقات الحقيقية لصفحةٍ محفوظة — وإلا توضيحية. */
  const realSlots = useQuery({
    queryKey: ["booking", "preview-slots", page?.slug],
    queryFn: () => {
      const from = new Date();
      const to = new Date(from);
      to.setDate(to.getDate() + 14);
      return api<{ days: BookingDay[] }>(
        `/public/booking/${encodeURIComponent(session.tenant.slug)}/${encodeURIComponent(page?.slug ?? "")}/slots?from=${from.toISOString()}&to=${to.toISOString()}`,
        { skipAuth: true },
      );
    },
    enabled: Boolean(live && page && page.slug === slug.trim()),
    retry: false,
    staleTime: 60_000,
  });
  const previewDays = realSlots.data?.days?.length
    ? realSlots.data.days
    : demoDays({
        durationMin: Number(durationMin) || 30,
        bufferMin: Number(bufferMin) || 0,
        windowStart,
        windowEnd,
      });

  return (
    <Modal
      title={page ? `تعديل — ${kindLabel}` : kindLabel}
      onClose={onClose}
      wide
      footer={
        <>
          <span className="tiny mute bp-missing">
            {missing.length > 0
              ? `ينقص: ${missing.join("، ")}`
              : "تُحفظ مسودةً تلقائيًّا حتى تحفظ الصفحة."}
          </span>
          <button className="btn" onClick={onClose}>
            إلغاء
          </button>
          <button
            className="btn btn-primary"
            onClick={() => {
              setError(null);
              save.mutate();
            }}
            disabled={save.isPending || missing.length > 0}
          >
            <Icon name="check" size={14} />
            {save.isPending
              ? "جارٍ الحفظ"
              : page
                ? "حفظ التعديلات"
                : "أنشئ الصفحة"}
          </button>
        </>
      }
    >
      <div
        ref={root}
        className="bp"
        style={{ "--bp-tone": KIND_TONE[kind] } as CSSProperties}
      >
        {/*
          صدرُ النموذج: النوع بلونه ورسمه، والاسم يُقرأ هنا كما يُكتب هناك.
          غلافُه ثابتٌ مُصمتٌ يمتدّ على عرض النافذة، فالأقسام تمرّ تحته ولا تُرى
          من خلفه ولا من جانبيه.
        */}
        <div ref={hero} className="bp-hero-wrap">
          <div className="bp-hero">
            <HeroArt />
            <div className="bp-hero-text">
              <span className="bp-hero-kind">
                <Icon name={kindMeta.icon} size={13} />
                {kindLabel}
              </span>
              <strong className="bp-hero-name">
                {name.trim() || "صفحةٌ بلا اسمٍ بعد"}
              </strong>
              <span className="tiny">{kindMeta.hint}</span>
            </div>
          </div>
        </div>

        {error && <div className="alert alert-danger">{error}</div>}

        {/* المسودة استُعيدت — يُقال ذلك ويُتاح التخلّي عنها. */}
        {draftRestored && (
          <div className="alert alert-info bp-draft">
            <Icon name="refresh" size={14} />
            <span>استُعيدت مسودةٌ لم تُحفظ من المرّة السابقة.</span>
            <button
              type="button"
              className="btn btn-sm"
              onClick={onDiscardDraft}
            >
              تجاهل المسودة
            </button>
          </div>
        )}

        <div className="bp-layout">
          <div className="bp-main">
            {/* ---------------- التعريف ---------------- */}
            <Section
              icon="link"
              title="التعريف"
              hint="ما يراه العميل أولًا: اسمٌ ورابطٌ ووصف."
            >
              <label className="field">
                <span className="label req">اسم الصفحة</span>
                <input
                  className="input"
                  value={name}
                  onChange={(e) => setName(e.target.value)}
                  placeholder={
                    place ? "حجز الاستوديو" : "استشارة تعارف — ٣٠ دقيقة"
                  }
                />
              </label>

              <label className="field">
                <span className="label req">الرابط</span>
                <div className="bp-slug">
                  <span className="tiny mute nowrap" dir="ltr">
                    /book/…/
                  </span>
                  <input
                    className="input"
                    value={slug}
                    onChange={(e) => setSlug(e.target.value)}
                    placeholder={place ? "studio" : "intro-30"}
                    dir="ltr"
                  />
                </div>
                {/*
                  الرابط لاتينيّ لأنه يُنسخ في رسالة ويُملى في مكالمة.
                  والعربية فيه تصير ثمانين حرفًا من الرموز لا يقرؤها أحد.
                */}
                <span className="tiny mute">
                  حروف لاتينية وأرقام وشرطات فقط.
                </span>
              </label>

              <label className="field span-2">
                <span className="label">وصف يقرؤه العميل (اختياري)</span>
                <textarea
                  className="textarea"
                  rows={2}
                  value={description}
                  onChange={(e) => setDescription(e.target.value)}
                  placeholder={
                    place
                      ? "استوديو مجهَّز بإضاءةٍ وخلفياتٍ — يُحجز بالساعة."
                      : "نتعرّف على احتياجك ونقترح الخطوة التالية."
                  }
                />
              </label>
            </Section>

            {/* ---------------- شكل الصفحة ---------------- */}
            <Section
              icon="expand"
              title="شكل الصفحة"
              hint="عاديةٌ تعرّف وتحجز من بيانات الصفحة، أو عرضٌ يُقرأ فصلًا فصلًا كإصدارات المنتجات."
            >
              <div className="field span-2">
                <div className="bp-choices">
                  <Choice
                    on={layout === "CLASSIC"}
                    icon="layers"
                    label="صفحة عادية"
                    hint="بطلٌ ومزايا وخدماتٌ ومعرض ثم الحجز — جاهزةٌ بلا محتوًى إضافي."
                    onClick={() => setLayout("CLASSIC")}
                  />
                  <Choice
                    on={layout === "SHOW"}
                    icon="zap"
                    label="صفحة عرض"
                    hint="عنوانٌ ضخم وأرقامٌ وفصولٌ بصورها ونقاطها وأسئلة — تكتب محتواها أدناه."
                    onClick={() => setLayout("SHOW")}
                  />
                </div>
              </div>
              {layout === "SHOW" && (
                <div className="field span-2">
                  <ShowEditor value={show} onChange={setShow} />
                </div>
              )}
            </Section>

            {/* ---------------- الوقت ---------------- */}
            <Section
              icon="clock"
              title="الوقت والتقويم"
              hint="من أين تُقرأ الأوقات الشاغرة، وكم حجزًا يقع في الوقت نفسه."
            >
              <label className="field">
                <span className="label req">التقويم</span>
                <select
                  className="select"
                  value={calendarId}
                  onChange={(e) => setCalendarId(e.target.value)}
                >
                  <option value="">اختر تقويمًا</option>
                  {usable.map((c) => (
                    <option key={c.id} value={c.id}>
                      {c.name}
                    </option>
                  ))}
                </select>
                <span className="tiny mute">
                  منه تُقرأ ساعات العمل والأوقات المحجوزة، وفيه يُكتب الحجز.
                </span>
              </label>

              {/*
                صاحب الوقت سؤالٌ للاستشارة لا للمكان.

                الاستشارة وقتُ شخصٍ بعينه يُحجز منه، أما الاستوديو فيُحجز هو —
                ومن يستقبل فيه شأنُ الجدولة الداخلية لا شأن الحاجز.
              */}
              {!place && (
                <label className="field">
                  <span className="label">صاحب الوقت</span>
                  <select
                    className="select"
                    value={ownerId}
                    onChange={(e) => setOwnerId(e.target.value)}
                  >
                    <option value="">بلا مسؤول</option>
                    {assignable.map((m) => (
                      <option key={m.userId} value={m.userId}>
                        {m.name}
                      </option>
                    ))}
                  </select>
                </label>
              )}

              {/*
                مدّة الحجز سؤالٌ للاستشارة لا للمكان.

                حجزُ المكان يختار العميل بدايتَه ونهايتَه، فالمدّة نتيجةٌ لا
                مُدخَل — ووحدةُ الشبكة تبقى ما اختير هنا افتراضًا.
              */}
              {!place && (
                <label className="field">
                  <span className="label req">مدة الحجز</span>
                  <select
                    className="select"
                    value={durationMin}
                    onChange={(e) => setDurationMin(e.target.value)}
                  >
                    {SLOT_MINUTES.map((m) => (
                      <option key={m} value={m}>
                        {durationLabel(m)}
                      </option>
                    ))}
                  </select>
                </label>
              )}

              {/* عدّادٌ لا حقلُ رقم: السعة تُزاد واحدةً واحدة وتُقرأ بلا تخمين. */}
              <div className="field">
                <span className="label">عدد الحجوزات المتزامنة</span>
                <div
                  className="bp-stepper"
                  role="group"
                  aria-label="عدد الحجوزات المتزامنة"
                >
                  <button
                    type="button"
                    className="btn btn-icon btn-sm"
                    disabled={capacity <= 1}
                    onClick={() => setCapacity((c) => Math.max(1, c - 1))}
                    aria-label="أنقص"
                  >
                    <Icon name="minus" size={13} />
                  </button>
                  <span className="bp-stepper-val num" aria-live="polite">
                    {capacity}
                  </span>
                  <button
                    type="button"
                    className="btn btn-icon btn-sm"
                    disabled={capacity >= 50}
                    onClick={() => setCapacity((c) => Math.min(50, c + 1))}
                    aria-label="زِد"
                  >
                    <Icon name="plus" size={13} />
                  </button>
                </div>
                <span className="tiny mute">
                  {capacity === 1
                    ? "حجزٌ واحدٌ في الوقت الواحد — كمستشارٍ لا يجلس مع اثنين."
                    : `حتى ${capacity} حجوزاتٍ تقع معًا في الوقت نفسه — كقاعةٍ تتّسع لأكثر من جلسة.`}
                </span>
              </div>

              <label className="field">
                <span className="label">فاصل بعد كل حجز</span>
                <div className="bp-unit">
                  <input
                    className="input num"
                    type="number"
                    min={0}
                    max={60}
                    value={bufferMin}
                    onChange={(e) => setBufferMin(e.target.value)}
                  />
                  <span className="tiny mute">دقيقة</span>
                </div>
                <span className="tiny mute">تفصل الحجز عمّا بعده.</span>
              </label>

              <label className="field">
                <span className="label">أقلّ مهلة قبل الحجز</span>
                <div className="bp-unit">
                  <input
                    className="input num"
                    type="number"
                    min={0}
                    max={168}
                    value={noticeHours}
                    onChange={(e) => setNoticeHours(e.target.value)}
                  />
                  <span className="tiny mute">ساعة</span>
                </div>
                <span className="tiny mute">لا يُحجز وقتٌ أقرب منها.</span>
              </label>

              <label className="field">
                <span className="label">مدى الحجز</span>
                <div className="bp-unit">
                  <input
                    className="input num"
                    type="number"
                    min={1}
                    max={120}
                    value={horizonDays}
                    onChange={(e) => setHorizonDays(e.target.value)}
                  />
                  <span className="tiny mute">يوم</span>
                </div>
                <span className="tiny mute">كم يومًا إلى الأمام تُعرض.</span>
              </label>

              {/*
                نافذة ساعات تخصّ الصفحة: «أستقبل من الثانية إلى السادسة» دون
                تعديل ساعات التقويم التي تشاركه صفحاتٌ أخرى. تتقاطع معها ولا
                تتجاوزها.
              */}
              <div className="field span-2">
                <span className="label">
                  {place ? "مواعيد عمل المكان" : "ساعات الاستقبال (اختياري)"}
                </span>
                <div className="bp-range">
                  <label className="bp-range-part">
                    <span className="tiny mute">من</span>
                    <input
                      className="input"
                      type="time"
                      value={windowStart}
                      onChange={(e) => setWindowStart(e.target.value)}
                    />
                  </label>
                  <Icon name="arrowRightLeft" size={14} />
                  <label className="bp-range-part">
                    <span className="tiny mute">إلى</span>
                    <input
                      className="input"
                      type="time"
                      value={windowEnd}
                      onChange={(e) => setWindowEnd(e.target.value)}
                    />
                  </label>
                </div>
                <span className="tiny mute">
                  فارغةً تأخذ ساعات التقويم
                  {calendar ? ` «${calendar.name}»` : ""} كما هي.
                </span>
              </div>
            </Section>

            {/* ---------------- ما يختاره العميل ---------------- */}
            <Section
              icon="target"
              title="ما يختاره العميل"
              hint="الغرضُ والوسيلة والمكان — تُعرض عليه خطوةً خطوة قبل الوقت."
            >
              <div className="field span-2">
                <span className="label">
                  {money ? "أغراض الحجز وأسعارها" : "أغراض الحجز"}
                </span>

                {purposes.length > 0 && (
                  <ul className="bp-purposes">
                    {purposes.map((x, i) => (
                      <li className="bp-purpose" key={`${x.name}-${i}`}>
                        <span className="bp-purpose-n num">{i + 1}</span>
                        <span className="bp-purpose-name">
                          {x.name}
                          {(x.serviceId || x.packageId) && (
                            <span className="bp-purpose-src">
                              {x.packageId ? "باقة" : "خدمة"}
                            </span>
                          )}
                        </span>
                        {money && (
                          <span
                            className={`bp-purpose-price num${x.price == null ? " is-free" : ""}`}
                          >
                            {x.price != null
                              ? `${x.price.toLocaleString("en-US")} ج.م${place ? ` / ${durationLabel(Number(priceUnitMin))}` : ""}`
                              : "بلا سعر"}
                          </span>
                        )}
                        <button
                          className="btn btn-sm btn-icon btn-danger-hover"
                          type="button"
                          onClick={() =>
                            setPurposes((prev) =>
                              prev.filter((_, k) => k !== i),
                            )
                          }
                          aria-label={`حذف ${x.name}`}
                        >
                          <Icon name="trash" size={13} />
                        </button>
                      </li>
                    ))}
                  </ul>
                )}

                <div className="bp-catalog">
                  <Icon name="layers" size={14} />
                  <select
                    className="select"
                    value=""
                    onChange={(e) => {
                      const v = e.target.value;
                      if (v) void addFromCatalog(v);
                    }}
                    aria-label="أضِف من كتالوج المبيعات"
                  >
                    <option value="">
                      أضِف من كتالوج المبيعات — خدمةً أو باقة…
                    </option>
                    {(catalogServices.data?.items ?? []).length > 0 && (
                      <optgroup label="الخدمات">
                        {(catalogServices.data?.items ?? []).map((sv) => (
                          <option key={sv.id} value={`s:${sv.id}`}>
                            {sv.name} —{" "}
                            {Number(sv.unitPrice).toLocaleString("en-US")} ج.م /{" "}
                            {sv.unit}
                          </option>
                        ))}
                      </optgroup>
                    )}
                    {(catalogPackages.data?.items ?? []).length > 0 && (
                      <optgroup label="الباقات">
                        {(catalogPackages.data?.items ?? []).map((pk) => (
                          <option key={pk.id} value={`p:${pk.id}`}>
                            {pk.name}
                          </option>
                        ))}
                      </optgroup>
                    )}
                  </select>
                </div>
                <div className="bp-add">
                  <input
                    className="input"
                    style={{ flex: 2, minWidth: "11rem" }}
                    value={purposeName}
                    onChange={(e) => setPurposeName(e.target.value)}
                    placeholder="أو اكتب غرضًا يدويًّا"
                    onKeyDown={(e) => {
                      if (e.key !== "Enter" || !purposeName.trim()) return;
                      e.preventDefault();
                      setPurposes((prev) => [
                        ...prev,
                        {
                          name: purposeName.trim(),
                          price:
                            money && purposePrice !== ""
                              ? Number(purposePrice)
                              : null,
                        },
                      ]);
                      setPurposeName("");
                      setPurposePrice("");
                    }}
                  />
                  {money && (
                    <div
                      className="bp-unit"
                      style={{ flex: 1, minWidth: "8rem" }}
                    >
                      <input
                        className="input num"
                        type="number"
                        min="0"
                        step="any"
                        value={purposePrice}
                        onChange={(e) => setPurposePrice(e.target.value)}
                        placeholder="السعر"
                      />
                      <span className="tiny mute nowrap">
                        {place
                          ? `ج.م / ${durationLabel(Number(priceUnitMin))}`
                          : "ج.م"}
                      </span>
                    </div>
                  )}
                  <button
                    className="btn btn-sm"
                    type="button"
                    disabled={!purposeName.trim() || purposes.length >= 12}
                    onClick={() => {
                      setPurposes((prev) => [
                        ...prev,
                        {
                          name: purposeName.trim(),
                          price:
                            money && purposePrice !== ""
                              ? Number(purposePrice)
                              : null,
                        },
                      ]);
                      setPurposeName("");
                      setPurposePrice("");
                    }}
                  >
                    <Icon name="plus" size={13} />
                    أضِف
                  </button>
                </div>
                <span className="tiny mute">
                  {money
                    ? "يختار العميل غرضه فيصل الحجز معروفَ سببه — والسعر يتبع غرضه. واتركها فارغةً لتسقط الخطوة."
                    : "يختار العميل غرضه فيصل الحجز معروفَ سببه. واتركها فارغةً لتسقط الخطوة من الصفحة."}
                </span>
              </div>

              {/* الوسيلة سؤالٌ للاستشارة: المكان يُحضَر إليه ولا يُحجز فيه أونلاين. */}
              {!place && (
                <div className="field span-2">
                  <span className="label req">وسيلة الحجز</span>
                  <div className="bp-choices">
                    {BOOKING_MODES.map((m) => {
                      const on = modes.includes(m);
                      return (
                        <Choice
                          key={m}
                          on={on}
                          icon={m === "ONSITE" ? "mapPin" : "video"}
                          label={BOOKING_MODE_LABELS[m]}
                          hint={
                            m === "ONSITE"
                              ? "يحضر العميل إلى مكانٍ تحدّده."
                              : "اجتماعٌ برابطٍ يُرسَل في التأكيد."
                          }
                          onClick={() =>
                            setModes((prev) => {
                              const next = on
                                ? prev.filter((x) => x !== m)
                                : [...prev, m];
                              /* واحدةٌ على الأقل: صفحةٌ بلا وسيلةٍ لا يُحجز عليها. */
                              return next.length ? next : prev;
                            })
                          }
                        />
                      );
                    })}
                  </div>
                  <span className="tiny mute">
                    المتاح منها يظهر للعميل ليختار — والمختارة تُكتب في الحجز
                    الناتج.
                  </span>
                </div>
              )}

              {modes.includes("ONSITE") && (
                <>
                  <label className="field">
                    <span className="label">
                      {place ? "عنوان المكان" : "مكان الحجز الحضوريّ"}
                    </span>
                    <input
                      className="input"
                      value={locationText}
                      onChange={(e) => setLocationText(e.target.value)}
                      placeholder="الاستوديو — التجمّع الخامس، الدور الثاني"
                    />
                  </label>
                  <label className="field">
                    <span className="label">موقعه على الخريطة</span>
                    <input
                      className="input num"
                      dir="ltr"
                      value={locationMapsUrl}
                      onChange={(e) => setLocationMapsUrl(e.target.value)}
                      placeholder="https://maps.google.com/…"
                    />
                    <span className="tiny mute">
                      يظهر للعميل زرَّ ملاحة، ويُنسخ إلى الحجز.
                    </span>
                  </label>
                </>
              )}

              {/*
                نمط المدّة يخصّ حجز المكان.

                الاستشارة موعدٌ بمدّةٍ ثابتة بطبعها، فعرضُ المبدّل عليها سؤالٌ
                بلا معنى. والمكان يُحجز ساعةً أو خمسًا — فله المبدّل وسقفُه.
              */}
              {place && (
                <div className="field span-2">
                  <span className="label req">كيف تُحجز المدّة؟</span>
                  <div className="bp-choices">
                    <Choice
                      on={durationMode === "FIXED"}
                      icon="clock"
                      label="مدّة ثابتة"
                      hint="كل حجزٍ بمدّةٍ واحدة — يختار العميل البداية فقط."
                      onClick={() => setDurationMode("FIXED")}
                    />
                    <Choice
                      on={durationMode === "RANGE"}
                      icon="arrowRightLeft"
                      label="مدًى يختاره العميل"
                      hint="من متى إلى متى — ضمن سقفٍ تحدّده."
                      onClick={() => setDurationMode("RANGE")}
                    />
                  </div>
                </div>
              )}

              {place && durationMode === "FIXED" && (
                <label className="field">
                  <span className="label req">مدة الحجز</span>
                  <select
                    className="select"
                    value={durationMin}
                    onChange={(e) => setDurationMin(e.target.value)}
                  >
                    {SLOT_MINUTES.map((m) => (
                      <option key={m} value={m}>
                        {durationLabel(m)}
                      </option>
                    ))}
                  </select>
                </label>
              )}

              {durationMode === "RANGE" && (
                <label className="field">
                  <span className="label req">أقصى مدّة للحجز</span>
                  <select
                    className="select"
                    value={maxDurationMin}
                    onChange={(e) => setMaxDurationMin(e.target.value)}
                  >
                    {[
                      "60",
                      "90",
                      "120",
                      "180",
                      "240",
                      "300",
                      "360",
                      "480",
                      "600",
                      "720",
                    ].map((m) => (
                      <option key={m} value={m}>
                        {durationLabel(Number(m))}
                      </option>
                    ))}
                  </select>
                  <span className="tiny mute">
                    سقفٌ يمنع أن يحجز واحدٌ اليومَ كلَّه.
                  </span>
                </label>
              )}
            </Section>

            {/* ---------------- نموذج العميل ---------------- */}
            <Section
              icon="user"
              title="نموذج العميل عند الحجز"
              hint="ابنِ ما يملؤه العميل من عناصر بطاقته — اسحب ورتّب وألزِم."
            >
              <div className="field span-2">
                <GuestFormBuilder
                  fields={guestFields}
                  onChange={setGuestFields}
                />
                <span className="tiny mute">
                  ما يُجمع هنا يُصبّ في بطاقة العميل المحتمل: الشركةُ شركتُه
                  والمدينةُ مدينتُه. والاسمُ والبريد لازمان — بهما يُرسَل
                  التأكيد ويُربط العميل.
                </span>
              </div>
            </Section>

            {/* ---------------- المال — للمدفوع وحجز المكان ---------------- */}
            {money && (
              <Section
                icon="wallet"
                title="المال"
                hint="يُعرض للعميل قبل الحجز ويُكتب في رسالة التأكيد — ولا يُحصَّل هنا."
              >
                {/* الوحدة تخصّ المدى: موعدٌ ثابتٌ سعرُه سعرُه، لا يُقسَّم. */}
                {place && (
                  <label className="field">
                    <span className="label">السعر لكل</span>
                    <select
                      className="select"
                      value={priceUnitMin}
                      onChange={(e) => setPriceUnitMin(e.target.value)}
                    >
                      {["15", "30", "45", "60", "90", "120"].map((m) => (
                        <option key={m} value={m}>
                          {durationLabel(Number(m))}
                        </option>
                      ))}
                    </select>
                    <span className="tiny mute">
                      به تُحسب قيمة المدّة المختارة — خمسُ ساعاتٍ بسعر الساعة
                      تُضرب خمسًا.
                    </span>
                  </label>
                )}

                {/* منزلقٌ يُقرأ بالعين: المقدَّم نسبةٌ تُرى لا رقمٌ يُخمَّن. */}
                <div className="field">
                  <span className="label">المقدَّم المطلوب</span>
                  <div className="bp-slider">
                    <input
                      type="range"
                      min={0}
                      max={100}
                      step={5}
                      value={Number(depositPercent) || 0}
                      onChange={(e) => setDepositPercent(e.target.value)}
                      aria-label="نسبة المقدَّم"
                    />
                    <span className="bp-slider-val num">
                      {Number(depositPercent) || 0}%
                    </span>
                  </div>
                  <span className="tiny mute">
                    {Number(depositPercent) > 0
                      ? "يظهر للعميل في التأكيد وفي رسالته — والتحصيل يدويٌّ حتى تُربط بوّابة دفع."
                      : "صفرٌ يعني لا مقدَّم — يُدفع كلُّ المبلغ في موعده."}
                  </span>
                </div>
              </Section>
            )}

            {/* ---------------- الشروط والسياسات ---------------- */}
            <Section
              icon="clipboard"
              title="الشروط والسياسات"
              hint="تُقرأ في الصفحة قبل الالتزام، وتُعاد في رسالة التأكيد."
            >
              <label className="field">
                <span className="label">الشروط والأحكام</span>
                <textarea
                  className="textarea"
                  rows={3}
                  value={priceNote}
                  onChange={(e) => setPriceNote(e.target.value)}
                  placeholder="المقدَّم يُحوَّل قبل الجلسة والباقي في المكان. ويُحضر العميل ما يلزم تصويره."
                />
              </label>
              <label className="field">
                <span className="label">سياسة الإلغاء والاسترداد</span>
                <textarea
                  className="textarea"
                  rows={3}
                  value={cancellationPolicy}
                  onChange={(e) => setCancellationPolicy(e.target.value)}
                  placeholder="الإلغاء قبل ٢٤ ساعة يُردّ معه المقدَّم كاملًا، وما دونها يُخصم."
                />
              </label>

              {/*
                شروطٌ إضافية بتكلفتها.

                «الساعة الإضافية ٥٠٠» ليست شرطًا يُقرأ فحسب — رقمٌ يُسأل
                عنه. فلكلّ شرطٍ نوعُه: نصٌّ، أو نصٌّ بنسبةٍ، أو بمبلغٍ.
                والمجانيةُ لا مالَ فيها فشرطُها نصٌّ لا غير.
              */}
              <div className="field span-2">
                <span className="label">شروط إضافية</span>
                <ExtraTermsEditor
                  terms={extraTerms}
                  money={money}
                  onChange={setExtraTerms}
                />
                <span className="tiny mute">
                  تُعرض للعميل قائمةً بتكلفتها وتُكتب في تأكيده — إعلامًا بما قد
                  يُطلب، لا بندًا يُضاف إلى القيمة الآن.
                </span>
              </div>
            </Section>

            {/* ---------------- المعرض ---------------- */}
            <Section
              icon="layers"
              title="صور وفيديو الصفحة"
              hint={
                place
                  ? "المكان يُحجز بصورته لا بوصفه."
                  : "تُعرض أعلى الصفحة قبل اختيار الوقت."
              }
            >
              <div className="field span-2">
                {media.length > 0 && (
                  <div className="bp-media">
                    {media.map((m, i) => (
                      <figure
                        className="bp-media-tile"
                        key={`${m.url.slice(0, 40)}-${i}`}
                      >
                        {m.kind === "IMAGE" ? (
                          <img src={m.url} alt={m.caption ?? ""} />
                        ) : (
                          <span className="bp-media-video">
                            <Icon name="video" size={18} />
                          </span>
                        )}
                        <figcaption className="tiny">
                          {m.caption || (m.kind === "VIDEO" ? "فيديو" : "صورة")}
                        </figcaption>
                        <button
                          type="button"
                          className="bp-media-del"
                          onClick={() =>
                            setMedia((prev) => prev.filter((_, x) => x !== i))
                          }
                          aria-label="حذف العنصر"
                        >
                          <Icon name="x" size={11} />
                        </button>
                      </figure>
                    ))}
                  </div>
                )}

                {/*
                  الصورة تُرفع، والفيديو رابطٌ.

                  الصور تُخزَّن في الصفحة نفسها فتظهر لمن يفتح الرابط بلا
                  حسابٍ ولا خادمِ ملفات. أما الفيديو فيبقى عند منصّته: رفعُه
                  إلى قاعدة بيانات ثِقلٌ بلا فائدة، ومشغّلُ يوتيوب أقدرُ على بثّه.
                */}
                <div className="bp-add">
                  <label
                    className={`bp-upload${uploading || media.length >= 12 ? " is-off" : ""}`}
                  >
                    <Icon name={uploading ? "refresh" : "plus"} size={14} />
                    <span>{uploading ? "جارٍ الرفع" : "ارفع صورة"}</span>
                    <input
                      type="file"
                      accept="image/*"
                      disabled={uploading || media.length >= 12}
                      onChange={async (e) => {
                        const file = e.target.files?.[0];
                        e.target.value = "";
                        if (!file) return;
                        if (file.size > 900_000) {
                          toast(
                            "الصورة أكبر من ٩٠٠ كيلوبايت — اضغطها أو اختر أصغر.",
                          );
                          return;
                        }
                        setUploading(true);
                        try {
                          const url = await readAsDataUrl(file);
                          setMedia((prev) => [
                            ...prev,
                            {
                              kind: "IMAGE",
                              url,
                              caption: mediaCaption.trim() || null,
                            },
                          ]);
                          setMediaCaption("");
                        } finally {
                          setUploading(false);
                        }
                      }}
                    />
                  </label>
                  <input
                    className="input num"
                    style={{ flex: 2, minWidth: "12rem" }}
                    dir="ltr"
                    value={mediaUrl}
                    onChange={(e) => setMediaUrl(e.target.value)}
                    placeholder="أو رابط فيديو — youtube.com/watch?v=…"
                  />
                  <button
                    className="btn btn-sm"
                    type="button"
                    disabled={!mediaUrl.trim() || media.length >= 12}
                    onClick={() => {
                      setMedia((prev) => [
                        ...prev,
                        {
                          kind: "VIDEO",
                          url: mediaUrl.trim(),
                          caption: mediaCaption.trim() || null,
                        },
                      ]);
                      setMediaUrl("");
                      setMediaCaption("");
                    }}
                  >
                    <Icon name="video" size={13} />
                    أضِف الفيديو
                  </button>
                  <input
                    className="input"
                    style={{ flex: 1, minWidth: "9rem" }}
                    value={mediaCaption}
                    onChange={(e) => setMediaCaption(e.target.value)}
                    placeholder="تعليق (اختياري)"
                  />
                </div>
                <span className="tiny mute">
                  الصور من جهازك حتى ٩٠٠ كيلوبايت للصورة، والفيديو من يوتيوب أو
                  فيميو — حتى اثني عشر عنصرًا.
                </span>
              </div>
            </Section>

            {/* ---------------- بعد الحجز ---------------- */}
            <Section
              icon="users"
              title="بعد الحجز"
              hint="ما يفعله النظام حين يحجز أحدهم."
            >
              <label className="bp-switch span-2">
                <input
                  type="checkbox"
                  checked={createLead}
                  onChange={(e) => setCreateLead(e.target.checked)}
                />
                <span className="bp-switch-track" aria-hidden="true">
                  <span className="bp-switch-knob" />
                </span>
                <span className="bp-switch-text">
                  <strong>أنشئ عميلًا محتملًا لمن يحجز</strong>
                  <span className="tiny mute">
                    لمن لم يُعرف بريده من قبل — ويُربط الحجز بملفه. ورسالة
                    التأكيد تصله في كل حال.
                  </span>
                </span>
              </label>
            </Section>
          </div>

          {/*
            معاينةٌ صغيرة على الجانب — وحيّةٌ بالحجم الحقيقي عند الطلب.

            البطاقة تُرسم من الحقول وتتغيّر مع كل حرف. ومن أراد الصفحة كما
            سيفتحها العميل فعلًا — يُمرّر ويختار وقتًا، على هاتفٍ أو حاسوب —
            فتحها بمقاسها الحقيقي داخل إطار.
          */}
          <aside className="bp-preview" aria-label="معاينة الصفحة">
            <span className="tiny mute bp-preview-label">
              <Icon name="eye" size={12} /> كما يراها العميل
            </span>
            <button
              type="button"
              className="bp-phone"
              onClick={() => setLive("mobile")}
              title="افتح المعاينة الحيّة"
            >
              {cover ? (
                <img className="bp-phone-cover" src={cover.url} alt="" />
              ) : (
                <div className="bp-phone-cover is-empty">
                  <Icon name={kindMeta.icon} size={22} />
                </div>
              )}
              <div className="bp-phone-body">
                <strong className="bp-phone-name">
                  {name.trim() || "اسم الصفحة"}
                </strong>
                <p className="tiny mute">
                  {description.trim() || "وصفٌ قصير يقرؤه العميل قبل أن يحجز."}
                </p>
                <div className="bp-phone-chips">
                  <span className="bp-phone-chip">
                    <Icon name="clock" size={11} />
                    {place && durationMode === "RANGE"
                      ? `حتى ${durationLabel(Number(maxDurationMin))}`
                      : durationLabel(Number(durationMin))}
                  </span>
                  {money && priceRange && (
                    <span className="bp-phone-chip is-tone">
                      <Icon name="wallet" size={11} />
                      {priceRange}
                      {place ? ` / ${durationLabel(Number(priceUnitMin))}` : ""}
                    </span>
                  )}
                  {!money && (
                    <span className="bp-phone-chip is-tone">
                      <Icon name="star" size={11} />
                      مجانًا
                    </span>
                  )}
                  {modes.map((m) => (
                    <span className="bp-phone-chip" key={m}>
                      <Icon
                        name={m === "ONSITE" ? "mapPin" : "video"}
                        size={11}
                      />
                      {BOOKING_MODE_LABELS[
                        m as keyof typeof BOOKING_MODE_LABELS
                      ] ?? m}
                    </span>
                  ))}
                  {capacity > 1 && (
                    <span className="bp-phone-chip">
                      <Icon name="users" size={11} />
                      {capacity} معًا
                    </span>
                  )}
                </div>
                {purposes.length > 0 && (
                  <ul className="bp-phone-list">
                    {purposes.slice(0, 4).map((x, i) => (
                      <li key={`${x.name}-${i}`}>
                        <span>{x.name}</span>
                        {money && x.price != null && (
                          <span className="num mute">
                            {x.price.toLocaleString("en-US")}
                          </span>
                        )}
                      </li>
                    ))}
                    {purposes.length > 4 && (
                      <li className="mute">و{purposes.length - 4} أخرى…</li>
                    )}
                  </ul>
                )}
                <span className="bp-phone-cta">
                  <Icon name="calendar" size={12} />
                  اختر وقتك
                </span>
              </div>
            </button>
            <span className="tiny mute bp-preview-hint">
              انقر البطاقة لتفتح المعاينة الحيّة — موبايل وديسكتوب.
            </span>
          </aside>
        </div>
      </div>

      {live && (
        <LivePreview
          device={live}
          onDevice={setLive}
          onClose={() => setLive(null)}
          info={previewInfo}
          days={previewDays}
          demo={!realSlots.data?.days?.length}
        />
      )}
    </Modal>
  );
}

/* ==========================================================================
   من حجز
   ========================================================================== */

interface BookingRow {
  id: string;
  guestName: string;
  guestEmail: string;
  guestPhone: string | null;
  note: string | null;
  /** إجابات نموذج الصفحة — بمفاتيح حقولها. */
  answers?: Record<string, BookingAnswer> | null;
  purpose: string | null;
  mode: string | null;
  durationMin: number | null;
  amount: number | null;
  depositAmount: number | null;
  startAt: string;
  endAt: string;
  termsAgreedAt: string | null;
  createdAt: string;
  leadId: string | null;
  leadStatus: string | null;
  dealId: string | null;
  quotationId: string | null;
  /** حسابٌ قائم — بالبريد نفسه، أو محتملٌ تحوّل. */
  accountId: string | null;
  accountName: string | null;
  /** مستنداتُ الحجز: العرضُ وعقودُه وفواتيرُه. */
  docs?: {
    id: string;
    number: string;
    status: string;
    title: string | null;
    total: number;
    contracts: Array<{
      id: string;
      number: string;
      status: string;
      signedAt: string | null;
    }>;
    invoices: Array<{
      id: string;
      number: string;
      status: string;
      total: number;
      paidAmount: number;
    }>;
  } | null;
}

/** لونٌ ثابتٌ لكل اسم — دائرةُ الحرفين تُعرف من بعيد قبل أن يُقرأ الاسم. */
function hueOf(name: string): number {
  let h = 0;
  for (const ch of name) h = (h * 31 + ch.charCodeAt(0)) % 360;
  return h;
}
const monthFmt = new Intl.DateTimeFormat("ar-EG-u-nu-latn", { month: "short" });
const weekdayFmt = new Intl.DateTimeFormat("ar-EG-u-nu-latn", {
  weekday: "long",
});

/**
 * من حجز — بطاقةٌ لكلِّ حجزٍ تُفتح على تفاصيله، وما يُصنع به.
 *
 * الاتصالُ والمراسلة من مكانهما — بأيقوناتِ النظام نفسِها: مكالمةٌ من
 * الكونسول، وواتساب، وبريدٌ يُكتب هنا. والصفقةُ تُفتح على حسابه إن كان له
 * حساب، وعلى المحتمل نفسِه إن كان محتملًا — والضيفُ يُضاف محتملًا أولًا.
 */
function BookingsModal({
  page,
  onClose,
}: {
  page: BookingPageView;
  onClose: () => void;
}) {
  const navigate = useNavigate();
  const toast = useToast();
  const queryClient = useQueryClient();
  const [open, setOpen] = useState<string | null>(null);
  /* معاينةُ مستندٍ من مكانه — العرضُ أو العقدُ أو الفاتورة في طبقةٍ فوق النافذة. */
  const [preview, setPreview] = useState<{
    path: string;
    title: string;
  } | null>(null);

  const list = useQuery({
    queryKey: ["booking", "bookings", page.id],
    queryFn: () => api<BookingRow[]>(`/booking/pages/${page.id}/bookings`),
  });
  const refresh = () => {
    void queryClient.invalidateQueries({
      queryKey: ["booking", "bookings", page.id],
    });
    void queryClient.invalidateQueries({ queryKey: ["leads"] });
    void queryClient.invalidateQueries({ queryKey: ["quotations"] });
  };

  const addLead = useMutation({
    mutationFn: (id: string) =>
      api<{ leadId: string }>(`/booking/pages/${page.id}/bookings/${id}/lead`, {
        method: "POST",
      }),
    onSuccess: () => {
      toast("أُضيف عميلًا محتملًا");
      refresh();
    },
    onError: (e) => toast(e instanceof Error ? e.message : "تعذّرت الإضافة."),
  });
  const openDeal = useMutation({
    mutationFn: (id: string) =>
      api<{ accountId: string; dealId: string; quotationId: string }>(
        `/booking/pages/${page.id}/bookings/${id}/deal`,
        { method: "POST", body: {} },
      ),
    onSuccess: () => {
      toast("فُتحت صفقةٌ وعرضُ سعر");
      refresh();
    },
    onError: (e) => toast(e instanceof Error ? e.message : "تعذّر فتح الصفقة."),
  });

  const rows = list.data ?? [];
  const labelOf = (key: string) =>
    page.guestFields?.find((f) => f.key === key)?.label ?? key;

  return (
    <Modal title={`حجوزات «${page.name}»`} onClose={onClose} wide>
      {list.isPending && <div className="skeleton" style={{ height: 120 }} />}
      {list.isError && <ErrorState error={list.error} />}

      {list.data && rows.length === 0 && (
        <div className="bk-none">
          <Icon name="calendar" size={28} />
          <strong>لا حجوزات على هذه الصفحة بعد</strong>
          <span className="tiny mute">
            أرسل رابطها لعميل — وستجد حجزه هنا بتفاصيله.
          </span>
        </div>
      )}

      {rows.length > 0 && (
        <>
          <div className="bk-summary">
            <span className="bk-sum">
              <strong className="num">{rows.length}</strong>
              <span className="tiny mute">حجزًا</span>
            </span>
            <span className="bk-sum">
              <strong className="num">
                {rows.filter((b) => b.accountId).length}
              </strong>
              <span className="tiny mute">لعملاء قائمين</span>
            </span>
            <span className="bk-sum">
              <strong className="num">
                {rows.filter((b) => b.quotationId).length}
              </strong>
              <span className="tiny mute">فُتحت صفقتُه</span>
            </span>
            <span className="bk-sum">
              <strong className="num">
                {rows
                  .reduce((t, b) => t + (b.amount ?? 0), 0)
                  .toLocaleString("en-US")}
              </strong>
              <span className="tiny mute">ج.م قيمةً</span>
            </span>
          </div>

          <div className="bk-list">
            {rows.map((b) => {
              const isOpen = open === b.id;
              const start = new Date(b.startAt);
              const linked = Boolean(b.accountId);
              const initials = b.guestName
                .trim()
                .split(/\s+/)
                .slice(0, 2)
                .map((w) => w[0])
                .join("");
              return (
                <article
                  key={b.id}
                  className={`bk${isOpen ? " is-open" : ""}${linked ? " is-account" : b.leadId ? " is-lead" : ""}`}
                >
                  <button
                    type="button"
                    className="bk-head"
                    onClick={() => setOpen(isOpen ? null : b.id)}
                    aria-expanded={isOpen}
                  >
                    <span className="bk-date" aria-hidden="true">
                      <strong className="bk-date-day num">
                        {start.getDate()}
                      </strong>
                      <span className="bk-date-month">
                        {monthFmt.format(start)}
                      </span>
                    </span>
                    <span
                      className="bk-avatar"
                      style={
                        { "--bk-hue": hueOf(b.guestName) } as CSSProperties
                      }
                      aria-hidden="true"
                    >
                      {initials}
                    </span>
                    <span className="bk-who">
                      <strong>{b.guestName}</strong>
                      <span className="tiny mute">
                        {weekdayFmt.format(start)} · {time(b.startAt)} —{" "}
                        {time(b.endAt)}
                        {b.purpose ? ` · ${b.purpose}` : ""}
                      </span>
                    </span>
                    <span className="bk-badges">
                      {b.amount != null && (
                        <span className="bk-amount num">
                          {b.amount.toLocaleString("en-US")} ج.م
                        </span>
                      )}
                      {linked ? (
                        <Badge kind="success">عميل قائم</Badge>
                      ) : b.leadId ? (
                        <Badge kind="neutral">عميل محتمل</Badge>
                      ) : (
                        <Badge kind="neutral">ضيف</Badge>
                      )}
                      {b.quotationId && (
                        <Badge kind="success">صفقة وعرض سعر</Badge>
                      )}
                    </span>
                    <Icon
                      name={isOpen ? "chevronUp" : "chevronDown"}
                      size={14}
                    />
                  </button>

                  {isOpen && (
                    <div className="bk-body">
                      {/* التواصل: الرقمُ والبريدُ وبجوارهما ما يُفعل بهما */}
                      <div className="bk-contact">
                        <div className="bk-contact-row">
                          <span className="bk-contact-ic">
                            <Icon name="phone" size={14} />
                          </span>
                          <span className="bk-contact-text">
                            <span className="tiny mute">الهاتف</span>
                            <span className="num" dir="ltr">
                              {b.guestPhone ? formatPhone(b.guestPhone) : "—"}
                            </span>
                          </span>
                        </div>
                        <div className="bk-contact-row">
                          <span className="bk-contact-ic">
                            <Icon name="mail" size={14} />
                          </span>
                          <span className="bk-contact-text">
                            <span className="tiny mute">البريد</span>
                            <span dir="ltr">{b.guestEmail}</span>
                          </span>
                        </div>
                        {/*
                          أيقوناتُ الوصول نفسُها التي في سائر النظام: مكالمةٌ من
                          الكونسول الراسي، وواتساب داخل قنوات ميتا — محادثةٌ قائمة أو
                          تُبدأ — لا إحالةً إلى الخارج، وSMS من المُرسِل، وبريدٌ من
                          صندوق الشركة مربوطًا بالسجل.
                        */}
                        <div className="bk-contact-actions">
                          <ReachIcons
                            name={b.guestName}
                            phone={b.guestPhone}
                            email={b.guestEmail}
                            link={{
                              leadId: b.leadId,
                              accountRefId: b.accountId,
                            }}
                          />
                        </div>
                      </div>

                      <div className="bk-facts">
                        <span className="bk-fact">
                          <Icon
                            name={b.mode === "ONSITE" ? "mapPin" : "video"}
                            size={13}
                          />
                          <span className="bk-fact-text">
                            <span className="tiny mute">الوسيلة</span>
                            <span>
                              {b.mode === "ONSITE"
                                ? "حضوريًّا"
                                : b.mode === "ONLINE"
                                  ? "أونلاين"
                                  : "—"}
                            </span>
                          </span>
                        </span>
                        <span className="bk-fact">
                          <Icon name="clock" size={13} />
                          <span className="bk-fact-text">
                            <span className="tiny mute">المدّة</span>
                            <span>
                              {b.durationMin
                                ? durationLabel(b.durationMin)
                                : "—"}
                            </span>
                          </span>
                        </span>
                        <span className="bk-fact">
                          <Icon name="target" size={13} />
                          <span className="bk-fact-text">
                            <span className="tiny mute">الغرض</span>
                            <span>{b.purpose ?? "—"}</span>
                          </span>
                        </span>
                        <span className="bk-fact">
                          <Icon name="wallet" size={13} />
                          <span className="bk-fact-text">
                            <span className="tiny mute">القيمة</span>
                            <span className="num">
                              {b.amount != null
                                ? `${b.amount.toLocaleString("en-US")} ج.م`
                                : "—"}
                            </span>
                          </span>
                        </span>
                        <span className="bk-fact">
                          <Icon name="receipt" size={13} />
                          <span className="bk-fact-text">
                            <span className="tiny mute">المقدَّم</span>
                            <span className="num">
                              {b.depositAmount != null
                                ? `${b.depositAmount.toLocaleString("en-US")} ج.م`
                                : "—"}
                            </span>
                          </span>
                        </span>
                        <span className="bk-fact">
                          <Icon name="clipboard" size={13} />
                          <span className="bk-fact-text">
                            <span className="tiny mute">الشروط</span>
                            <span>
                              {b.termsAgreedAt
                                ? `وافق — ${dateTime(b.termsAgreedAt)}`
                                : "لا شروطَ حين حجز"}
                            </span>
                          </span>
                        </span>
                        <span className="bk-fact">
                          <Icon name="calendar" size={13} />
                          <span className="bk-fact-text">
                            <span className="tiny mute">وقت الحجز</span>
                            <span>{dateTime(b.createdAt)}</span>
                          </span>
                        </span>
                        <span className="bk-fact">
                          <Icon name={linked ? "building" : "user"} size={13} />
                          <span className="bk-fact-text">
                            <span className="tiny mute">
                              {linked
                                ? "الحساب"
                                : b.leadId
                                  ? "العميل المحتمل"
                                  : "العميل"}
                            </span>
                            {linked ? (
                              <button
                                type="button"
                                className="link bk-link"
                                onClick={() =>
                                  navigate(`/crm/accounts/${b.accountId}`)
                                }
                              >
                                {b.accountName}
                                {b.leadStatus === "CONVERTED"
                                  ? " — تحوّل من محتمل"
                                  : ""}
                              </button>
                            ) : b.leadId ? (
                              <button
                                type="button"
                                className="link bk-link"
                                onClick={() =>
                                  navigate(`/crm/leads/${b.leadId}`)
                                }
                              >
                                {b.guestName} — عميل محتمل
                              </button>
                            ) : (
                              <span>لم يُضف بعد</span>
                            )}
                          </span>
                        </span>
                        <span className="bk-fact is-action">
                          <Icon name="briefcase" size={13} />
                          <span className="bk-fact-text">
                            <span className="tiny mute">الصفقة</span>
                            {linked || b.leadId ? (
                              b.quotationId ? (
                                <button
                                  type="button"
                                  className="btn btn-sm btn-primary"
                                  onClick={() =>
                                    navigate(
                                      linked
                                        ? `/crm/accounts/${b.accountId}`
                                        : `/crm/leads/${b.leadId}`,
                                    )
                                  }
                                >
                                  افتح الصفقة وعرض السعر
                                </button>
                              ) : (
                                <button
                                  type="button"
                                  className="btn btn-sm btn-primary"
                                  disabled={openDeal.isPending}
                                  onClick={() => openDeal.mutate(b.id)}
                                >
                                  افتح صفقةً وعرضَ سعر
                                </button>
                              )
                            ) : (
                              <button
                                type="button"
                                className="btn btn-sm btn-primary"
                                disabled={addLead.isPending}
                                onClick={() => addLead.mutate(b.id)}
                              >
                                أضف كعميل محتمل
                              </button>
                            )}
                          </span>
                        </span>
                        {b.answers &&
                          Object.entries(b.answers).map(([k, v]) => (
                            <span className="bk-fact" key={k}>
                              <Icon name="note" size={13} />
                              <span className="bk-fact-text">
                                <span className="tiny mute">{labelOf(k)}</span>
                                {typeof v === "string" ? (
                                  <span>{v}</span>
                                ) : (
                                  <a
                                    className="link"
                                    href={v.url}
                                    download={v.name}
                                  >
                                    <Icon name="paperclip" size={12} /> {v.name}
                                  </a>
                                )}
                              </span>
                            </span>
                          ))}
                        {b.note && (
                          <span className="bk-fact is-wide">
                            <Icon name="chat" size={13} />
                            <span className="bk-fact-text">
                              <span className="tiny mute">الموضوع</span>
                              <span className="bk-fact-long">{b.note}</span>
                            </span>
                          </span>
                        )}
                      </div>

                      {/*
                        دورةُ الحجز على بطاقاته: العرضُ الذي فُتح منه، ثم عقدُه حين
                        يُنشأ، ثم فاتورتُه حين تصدر — كلٌّ بحالته ومعاينتِه وبابِه.
                        وما لم يقم بعدُ يُقال إنه يظهر هنا حين يقوم.
                      */}
                      {b.docs && (
                        <div className="bk-docs">
                          <div className="bk-doc is-quote">
                            <span className="bk-doc-ic">
                              <Icon name="briefcase" size={14} />
                            </span>
                            <span className="bk-doc-text">
                              <strong>
                                عرض سعر{" "}
                                <span className="num">{b.docs.number}</span>
                              </strong>
                              <span className="tiny mute">
                                {b.docs.title ? `${b.docs.title} · ` : ""}
                                {money(b.docs.total)}
                              </span>
                            </span>
                            {QUOTATION_STATUS_MAP[
                              b.docs.status as keyof typeof QUOTATION_STATUS_MAP
                            ] && (
                              <Badge
                                kind={
                                  QUOTATION_STATUS_MAP[
                                    b.docs
                                      .status as keyof typeof QUOTATION_STATUS_MAP
                                  ].kind
                                }
                              >
                                {
                                  QUOTATION_STATUS_MAP[
                                    b.docs
                                      .status as keyof typeof QUOTATION_STATUS_MAP
                                  ].label
                                }
                              </Badge>
                            )}
                            <span className="bk-doc-actions">
                              <button
                                type="button"
                                className="btn btn-icon btn-sm"
                                title="معاينة العرض"
                                aria-label="معاينة العرض"
                                onClick={() =>
                                  setPreview({
                                    path: `/print/quotation/${b.docs!.id}`,
                                    title: "معاينة عرض السعر",
                                  })
                                }
                              >
                                <Icon name="eye" size={13} />
                              </button>
                              <button
                                type="button"
                                className="btn btn-sm"
                                onClick={() =>
                                  navigate(`/sales/quotations/${b.docs!.id}`)
                                }
                              >
                                افتح
                              </button>
                            </span>
                          </div>
                          {b.docs.contracts.map((c) => (
                            <div className="bk-doc is-contract" key={c.id}>
                              <span className="bk-doc-ic">
                                <Icon name="clipboard" size={14} />
                              </span>
                              <span className="bk-doc-text">
                                <strong>
                                  عقد <span className="num">{c.number}</span>
                                </strong>
                                <span className="tiny mute">
                                  {c.signedAt
                                    ? `موقَّع في ${date(c.signedAt)}`
                                    : "لم يُوقَّع بعد"}
                                </span>
                              </span>
                              {CONTRACT_STATUS_MAP[
                                c.status as keyof typeof CONTRACT_STATUS_MAP
                              ] && (
                                <Badge
                                  kind={
                                    CONTRACT_STATUS_MAP[
                                      c.status as keyof typeof CONTRACT_STATUS_MAP
                                    ].kind
                                  }
                                >
                                  {
                                    CONTRACT_STATUS_MAP[
                                      c.status as keyof typeof CONTRACT_STATUS_MAP
                                    ].label
                                  }
                                </Badge>
                              )}
                              <span className="bk-doc-actions">
                                <button
                                  type="button"
                                  className="btn btn-icon btn-sm"
                                  title="معاينة العقد"
                                  aria-label="معاينة العقد"
                                  onClick={() =>
                                    setPreview({
                                      path: `/print/contract/${c.id}`,
                                      title: "معاينة العقد",
                                    })
                                  }
                                >
                                  <Icon name="eye" size={13} />
                                </button>
                                <button
                                  type="button"
                                  className="btn btn-sm"
                                  onClick={() =>
                                    navigate(`/sales/contracts/${c.id}`)
                                  }
                                >
                                  افتح
                                </button>
                              </span>
                            </div>
                          ))}
                          {b.docs.invoices.map((inv) => (
                            <div className="bk-doc is-invoice" key={inv.id}>
                              <span className="bk-doc-ic">
                                <Icon name="receipt" size={14} />
                              </span>
                              <span className="bk-doc-text">
                                <strong>
                                  فاتورة{" "}
                                  <span className="num">{inv.number}</span>
                                </strong>
                                <span className="tiny mute">
                                  {money(inv.total)} — المسدَّد{" "}
                                  {money(inv.paidAmount)}
                                </span>
                              </span>
                              {INVOICE_STATUS_MAP[
                                inv.status as keyof typeof INVOICE_STATUS_MAP
                              ] && (
                                <Badge
                                  kind={
                                    INVOICE_STATUS_MAP[
                                      inv.status as keyof typeof INVOICE_STATUS_MAP
                                    ].kind
                                  }
                                >
                                  {
                                    INVOICE_STATUS_MAP[
                                      inv.status as keyof typeof INVOICE_STATUS_MAP
                                    ].label
                                  }
                                </Badge>
                              )}
                              <span className="bk-doc-actions">
                                <button
                                  type="button"
                                  className="btn btn-icon btn-sm"
                                  title="معاينة الفاتورة"
                                  aria-label="معاينة الفاتورة"
                                  onClick={() =>
                                    setPreview({
                                      path: `/print/invoice/${inv.id}`,
                                      title: "معاينة الفاتورة",
                                    })
                                  }
                                >
                                  <Icon name="eye" size={13} />
                                </button>
                                <button
                                  type="button"
                                  className="btn btn-sm"
                                  onClick={() =>
                                    navigate(`/finance/invoices/${inv.id}`)
                                  }
                                >
                                  افتح
                                </button>
                              </span>
                            </div>
                          ))}
                          {b.docs.contracts.length === 0 && (
                            <span className="tiny mute bk-docs-hint">
                              {b.docs.status === "ACCEPTED"
                                ? "قُبل العرض — العقدُ ثم الفاتورةُ يظهران هنا حين يُنشآن منه."
                                : "حين يُقبل العرض ويُنشأ عقدُه وتصدر فاتورتُه تظهر هنا بحالتها."}
                            </span>
                          )}
                        </div>
                      )}
                    </div>
                  )}
                </article>
              );
            })}
          </div>
        </>
      )}

      <p className="tiny mute mt-3">أُنشئت الصفحة في {date(page.createdAt)}.</p>

      {preview && (
        <DocPreview
          path={preview.path}
          title={preview.title}
          onClose={() => setPreview(null)}
        />
      )}
    </Modal>
  );
}
